import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { prism } from "react-syntax-highlighter/dist/esm/styles/prism";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useWorkingHistoryIdSet } from "../contexts/UserStatusContext";
import config from "../config";
import plusIcon from "../public/img/plus.svg";
import arrow from "../public/img/arrow.svg";
import copyIcon from "../public/img/copy.svg";
import ltLogo from "../public/img/LT_logo.svg";
import historyIconDark from "../public/img/history_icon_dark.svg";
import globeIcon from "../public/img/globe.svg";
import globeOffIcon from "../public/img/globe_off.svg";
import openaiLogo from "../public/img/openai_logo_dark.svg";
import downCaretIcon from "../public/img/down_caret.svg";
import "./Chat.css";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Typography from "@tiptap/extension-typography";
import { Markdown } from "tiptap-markdown";
import PromptToolsButton from "../components/PromptToolsButton";
import UserSelector from "../components/UserSelector";

// Helper to construct WebSocket URL
const getWebSocketURL = (chatbot) => {
  const baseUrl = config.api.baseUrl;
  let wsUrl = baseUrl.replace(/^http/, "ws");

  wsUrl += "/chatbot/ws";

  return wsUrl;
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const ANNOTATION_TOKEN_REGEX = /[【〖[]\s*(\d+)(?:\s*†[^\]】〗]*)?[】〗\]]/g;
const ANNOTATION_TEXT_ONLY_REGEX = /^[【〖[]\s*\d+(?:\s*†[^\]】〗]*)?[】〗\]]$/;
const SIMPLE_CITATION_REGEX = /(^|[^[])\[(\d+)\](?!\()/g;
const NUMERIC_LINK_TEXT_REGEX = /^\d+$/;
const SUPPORTED_CHAT_PROVIDERS = new Set([
  "openai",
  "google",
  "anthropic",
  "perplexity",
]);
const PROVIDER_ORDER = [
  "openai",
  "anthropic",
  "google",
  "perplexity",
];
const PROVIDER_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  perplexity: "Perplexity",
};

/**
 * Parse <think>...</think> blocks from message content.
 * Returns { thinkingContent, responseContent, isThinking }
 * - isThinking: true if we have an opening <think> but no closing </think> (still streaming thinking)
 * - thinkingContent: text inside the <think> tags
 * - responseContent: text after the </think> tag
 */
const parseThinkingContent = (content) => {
  if (!content || typeof content !== "string") {
    return { thinkingContent: null, responseContent: content || "", isThinking: false };
  }

  const openTag = "<think>";
  const closeTag = "</think>";
  const openIdx = content.indexOf(openTag);

  if (openIdx === -1) {
    return { thinkingContent: null, responseContent: content, isThinking: false };
  }

  const afterOpen = openIdx + openTag.length;
  const closeIdx = content.indexOf(closeTag, afterOpen);

  if (closeIdx === -1) {
    // Still thinking — no closing tag yet
    return {
      thinkingContent: content.slice(afterOpen),
      responseContent: "",
      isThinking: true,
    };
  }

  // Thinking complete
  const thinkingContent = content.slice(afterOpen, closeIdx);
  const responseContent = content.slice(closeIdx + closeTag.length).trimStart();

  return { thinkingContent, responseContent, isThinking: false };
};

/** Animated "Improving prompt..." label with cycling dots */
function ImprovingDotsLabel() {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => setDots((d) => (d === 3 ? 1 : d + 1)), 500);
    return () => clearTimeout(timer);
  }, [dots]);
  return (
    <span className="prompt-improving-dots">
      Improving prompt{".".repeat(dots)}
    </span>
  );
}

const Chat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const workingHistoryIds = useWorkingHistoryIdSet();

  // Parse URL parameters first, before any state that depends on them
  const searchParams = new URLSearchParams(location.search);
  const chatbotId = searchParams.get("id");
  const historyIdParam = searchParams.get("historyId");
  const messageTimestampParam = searchParams.get("messageTimestamp");
  const isComingFromSearch = !!(historyIdParam && messageTimestampParam);
  
  const [chatbot, setChatbot] = useState(null);
  const [loadingChatbot, setLoadingChatbot] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [, setInputText] = useState("");
  const [messages, setMessages] = useState([]);
  // const [isTyping, setIsTyping] = useState(false); // Kept for potential future use, but display is removed
  
  // Theme state for code blocks
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  
  // Message pagination state
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const loadingOlderMessagesRef = useRef(false);
  const adjustingScrollRef = useRef(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [, setTotalMessageCount] = useState(0);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const autoScrollRafRef = useRef(null);
  const lastAutoScrollTopRef = useRef(0);

  const wsInstance = useRef(null);
  const lastSeenSeqRef = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [, setCurrentFunctionCall] = useState(null);
  const [, setCurrentWebSearch] = useState(null);
  const [, setCurrentCodeInterpreter] = useState(null);
  const [isResponding, setIsResponding] = useState(false); // Track if chatbot is responding
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false); // Track if prompt tool is generating
  const [copiedMessageId, setCopiedMessageId] = useState(null); // Track which message was copied
  const [editingMessageId, setEditingMessageId] = useState(null); // Track which message is being edited
  const [editedContent, setEditedContent] = useState(""); // Store edited content
  const [expandedPrompts, setExpandedPrompts] = useState(new Set()); // Track which revised prompts are expanded
  const [expandedThinking, setExpandedThinking] = useState(new Set()); // Track which thinking blocks are expanded
  // TipTap Editor initialization
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: true,
        orderedList: true,
        heading: true,
        codeBlock: true,
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Typography,
      Placeholder.configure({
        placeholder: "Say anything...",
      }),
      Markdown.configure({
        html: false,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "chat-input tiptap-editor",
      },
      handleKeyDown(view, event) {
        // Shift+Enter or Cmd/Ctrl+Enter -> create new line
        if ((event.shiftKey || event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();

          // Don't use editor?.isActive if editor isn't ready yet
          if (!editor) {
            return false;
          }

          try {
            const inList =
              editor.isActive("bulletList") ||
              editor.isActive("orderedList");
            const inCode = editor.isActive("codeBlock");

            if (inList || inCode) {
              editor.chain().focus().setHardBreak().run();
              return true;
            }

            editor.chain().focus().splitBlock().setParagraph().run();
            return true;
          } catch (e) {
            console.error("Error handling new line:", e);
            return false;
          }
        }

        // Enter (no modifiers) -> send, except when inside list/code blocks
        if (event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
          if (!editor) {
            return false;
          }

          try {
            if (
              editor.isActive("bulletList") ||
              editor.isActive("orderedList") ||
              editor.isActive("codeBlock")
            ) {
              return false; // allow default behavior for lists/code
            }
          } catch (e) {
            // If we can't check the active state, just send
          }

          if (!isConnected) return true;
          event.preventDefault();
          handleSendFromEditor();
          return true;
        }

        return false;
      },
    },
  });

  // Track editor content to enable/disable send button reliably
  const [editorHasContent, setEditorHasContent] = useState(false);

  useEffect(() => {
    if (!editor) return;
    const update = () => {
      try {
        const txt = editor.getText ? editor.getText().trim() : "";
        setEditorHasContent(txt.length > 0);
      } catch (e) {
        setEditorHasContent(false);
      }
    };
    editor.on("update", update);
    editor.on("selectionUpdate", update);
    update();
    return () => {
      try {
        editor.off("update", update);
        editor.off("selectionUpdate", update);
      } catch (e) {}
    };
  }, [editor]);

  const getEditorMarkdown = () => {
    if (!editor) return "";
    try {
      // Prefer Markdown serialization from the extension if available
      const md = editor?.storage?.markdown?.getMarkdown
        ? editor.storage.markdown.getMarkdown()
        : null;
      if (md != null) return md;
      return editor.getText();
    } catch (e) {
      return editor.getText();
    }
  };

  const clearEditor = () => {
    if (editor) editor.commands.clearContent(true);
  };

  // New: replace editor content with improved text
  const replaceEditorContent = (newText) => {
    if (!editor) return;
    const text = newText || "";
    try {
      // Prefer markdown-aware setter if available
      if (editor.commands?.setMarkdown) {
        editor.commands.setMarkdown(text);
        return;
      }
    } catch (_) {}
    try {
      editor.commands.setContent(text);
    } catch (_) {
      editor.commands.clearContent(true);
      editor.commands.insertContent(text);
    }
  };

  // New: handler to call backend improve API and update editor
  const handleImproveAction = async (action) => {
    try {
      const currentText = editor?.storage?.markdown?.getMarkdown
        ? editor.storage.markdown.getMarkdown()
        : editor?.getText?.() || "";

      // Disable editor and show loading state
      setIsImprovingPrompt(true);
      if (editor) editor.setEditable(false);

      // Immediately clear editor to give live streaming effect
      replaceEditorContent("");

      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.improveStream}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ action, text: currentText }),
        }
      );

      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({}));
        setChatError(data.error || "Failed to improve prompt.");
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffered = "";
      let accumVisible = "";
      let firstDeltaReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = value ? decoder.decode(value, { stream: true }) : "";
        buffered += chunk;

        // Process newline-delimited JSON messages (SSE-friendly)
        let idx;
        while ((idx = buffered.indexOf("\n")) !== -1) {
          const line = buffered.slice(0, idx).trim();
          buffered = buffered.slice(idx + 1);
          if (!line) continue;
          try {
            const payload = line.startsWith("data:")
              ? line.slice(5).trim()
              : line;
            if (!payload) continue;
            const msg = JSON.parse(payload);
            if (msg.delta) {
              if (!firstDeltaReceived) {
                firstDeltaReceived = true;
                setIsImprovingPrompt(false);
              }
              accumVisible += msg.delta;
              const unescaped = accumVisible
                .replace(/\\\\/g, "\\")
                .replace(/\\"/g, '"')
                .replace(/\\t/g, "\t")
                .replace(/\\n/g, "\n");
              replaceEditorContent(unescaped);
            }
            if (msg.done) {
              replaceEditorContent(msg.rewritten_prompt || msg.text || "");
            }
            if (msg.error) {
              setChatError(msg.error);
            }
          } catch (e) {
            // ignore malformed lines
          }
        }
      }
    } catch (err) {
      setChatError("Network error while improving prompt.");
      console.error(err);
    } finally {
      setIsImprovingPrompt(false);
      if (editor) editor.setEditable(true);
      editor?.commands?.focus();
    }
  };

  const messagesEndRef = useRef(null);
  const prevChatbotIdRef = useRef(null);
  const currentBotMessageId = useRef(null);
  const responseSegmentStateRef = useRef({});
  const activeFunctionMessageByResponseRef = useRef({});
  const fileInputRef = useRef(null); // For handling file input clicks
  const [attachedFiles, setAttachedFiles] = useState([]); // To hold file objects for submission
  const [attachmentPreviews, setAttachmentPreviews] = useState([]); // For rendering previews with object URLs
  const [isDraggingFile, setIsDraggingFile] = useState(false); // Track if user is dragging files over the page

  // Chat history sidebar state
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyCards, setHistoryCards] = useState([]);
  const [loadingHistoryCards, setLoadingHistoryCards] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const loadingMoreHistoryRef = useRef(false); // Immediate check to prevent race conditions
  const lastLoadTimeRef = useRef(0); // Track last load time for cooldown
  const [historyError, setHistoryError] = useState(null);
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const currentHistoryIdRef = useRef(null);
  // Tracks the most recent history the user (or auto-loader) asked us to load.
  // handleSelectHistoryCard sets this synchronously on entry; when its async
  // fetch resolves, it bails out if pendingHistoryLoadRef no longer matches
  // its own cardId — meaning another load was kicked off in the meantime.
  // This is what stops the "ping-pong between two histories" bug where the
  // auto-loader's in-flight fetch clobbers the user's later click.
  const pendingHistoryLoadRef = useRef(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const historyListRef = useRef(null);
  const [loadingFromSearch, setLoadingFromSearch] = useState(isComingFromSearch);
  const [targetMessageTimestamp, setTargetMessageTimestamp] = useState(messageTimestampParam || null);
  const messageRefs = useRef({});
  
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cardId: null,
  });
  const [shareMenu, setShareMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cardId: null,
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [webSearchEnabled, setWebSearchEnabled] = useState(true);
  const [expandedAgentActivity, setExpandedAgentActivity] = useState(new Set());
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedModelProvider, setSelectedModelProvider] = useState("");
  const [providerLock, setProviderLock] = useState(null);
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const modelMenuRef = useRef(null);

  // One-time cleanup of the old surface-swap localStorage key (removed in this refactor)
  useEffect(() => { localStorage.removeItem("chatSelectedTarget"); }, []);

  const modelOptions = useMemo(() => {
    const supportsOnlyOpenAI = Boolean(chatbot?.vector_store_id);
    const normalizedProviderLock = providerLock
      ? String(providerLock).toLowerCase()
      : null;
    return (availableModels || []).filter((model) => {
      const provider = String(model?.provider || "").toLowerCase();
      if (!model?.id || !SUPPORTED_CHAT_PROVIDERS.has(provider)) return false;
      if (supportsOnlyOpenAI && provider !== "openai") return false;
      if (normalizedProviderLock && provider !== normalizedProviderLock)
        return false;
      return true;
    });
  }, [availableModels, chatbot?.vector_store_id, providerLock]);

  const selectedModelMeta = useMemo(() => {
    return modelOptions.find((m) => m.id === selectedModel) || null;
  }, [modelOptions, selectedModel]);

  const groupedModelOptions = useMemo(() => {
    const groups = new Map();
    modelOptions.forEach((model) => {
      const provider = String(model.provider || "").toLowerCase();
      if (!provider) return;
      if (!groups.has(provider)) groups.set(provider, []);
      groups.get(provider).push(model);
    });

    const orderedProviders = [
      ...PROVIDER_ORDER.filter((provider) => groups.has(provider)),
      ...Array.from(groups.keys()).filter(
        (provider) => !PROVIDER_ORDER.includes(provider)
      ),
    ];

    return orderedProviders.map((provider) => ({
      provider,
      label: PROVIDER_LABELS[provider] || provider,
      options: groups.get(provider) || [],
    }));
  }, [modelOptions]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.dashboard.models}`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (response.ok && data.success && Array.isArray(data.data)) {
          setAvailableModels(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch model options for chat:", error);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    if (!chatbot) return;
    const defaultProvider = String(chatbot.provider || "openai").toLowerCase();
    setSelectedModel(chatbot.model || "");
    setSelectedModelProvider(defaultProvider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbot?._id, chatbot?.model, chatbot?.provider]);

  useEffect(() => {
    if (!modelOptions.length) return;
    const selectedStillValid = modelOptions.some((m) => m.id === selectedModel);
    if (selectedStillValid) return;

    // Deleted/unavailable model: prefer the chatbot's configured model, then
    // the top-ranked model of the chatbot's own provider, then the first
    // option overall (modelOptions is rank-sorted by the backend).
    const preferredDefault = modelOptions.find((m) => m.id === chatbot?.model);
    const chatbotProvider = String(chatbot?.provider || "").toLowerCase();
    const providerFallback = modelOptions.find(
      (m) => String(m.provider || "").toLowerCase() === chatbotProvider
    );
    const fallback = preferredDefault || providerFallback || modelOptions[0];
    if (fallback) {
      setSelectedModel(fallback.id);
      setSelectedModelProvider(String(fallback.provider || "").toLowerCase());
    }
  }, [modelOptions, selectedModel, chatbot?.model, chatbot?.provider]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(event.target)) {
        setIsModelMenuOpen(false);
      }
    };

    if (isModelMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isModelMenuOpen]);

  // Format stored UTC ISO into local display by subtracting 7 hours first, then formatting
  // MM/DD/YYYY h:mm:ss AM/PM (no TZ suffix). This matches MST conversion per your requirement.
  const formatMSTFromUTC = (isoString) => {
    try {
      const parsed = new Date(isoString);
      if (isNaN(parsed.getTime())) return "";
      // subtract 7 hours from the parsed UTC value
      const adjusted = new Date(parsed.getTime() - 7 * 60 * 60 * 1000);
      // format in user's current local timezone
      return adjusted.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "";
    }
  };

  // Format history card time: if locally generated, show local time directly; else convert from UTC to MST
  const formatHistoryCardTime = (card) => {
    const ts = card?.updated_at;
    if (!ts) return "";
    if (card?.fromLocal) {
      try {
        return new Date(ts).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch (e) {
        return "";
      }
    }
    return formatMSTFromUTC(ts);
  };

  // Effect for cleaning up object URLs on unmount
  useEffect(() => {
    return () => {
      // When Chat component unmounts, revoke any lingering URLs to prevent memory leaks
      attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // IMPORTANT: Empty dependency array means this runs only on mount and cleanup on unmount.

  // New component for styled code blocks with header and copy button
  const StyledCodeBlock = ({ language, children }) => {
    const [copySuccess, setCopySuccess] = useState("");
    const [showPreview, setShowPreview] = useState(false); // New state for HTML preview
    const codeString = String(children).replace(/\n$/, "");

    const isHtml = language?.toLowerCase() === "html";

    const handleCopy = () => {
      navigator.clipboard.writeText(codeString).then(
        () => {
          setCopySuccess("Copied!");
          setTimeout(() => setCopySuccess(""), 2000);
        },
        () => {
          setCopySuccess("Failed");
          setTimeout(() => setCopySuccess(""), 2000);
        }
      );
    };

    const togglePreview = () => {
      setShowPreview(!showPreview);
    };

    return (
      <div className="styled-code-block-container">
        <div className="styled-code-block-header">
          <span className="styled-code-block-language">
            {language || "code"}
          </span>
          <div className="styled-code-block-header-buttons">
            {isHtml && (
              <button
                onClick={togglePreview}
                className="styled-code-block-toggle-button"
                title={showPreview ? "Show Code" : "Preview HTML"}
              >
                {showPreview ? "Show Code" : "Preview HTML"}
              </button>
            )}
            <span
              onClick={handleCopy}
              className="styled-code-block-copy-button"
              title={copySuccess || "Copy code"}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && handleCopy()
              }
            >
              <img src={copyIcon} alt="Copy code" className="copy-icon-svg" />
            </span>
          </div>
        </div>
        {isHtml && showPreview ? (
          <iframe
            className="styled-code-block-html-preview"
            srcDoc={codeString}
            sandbox="allow-scripts"
            title="HTML Preview"
          />
        ) : (
          <SyntaxHighlighter
            style={isDarkMode ? vscDarkPlus : prism}
            language={language}
            // PreTag="div" // Keeping this commented, <pre> is better for themes
            customStyle={{ margin: 0, borderRadius: "0 0 6px 6px" }}
          >
            {codeString}
          </SyntaxHighlighter>
        )}
      </div>
    );
  };

  const getUserInitials = () => {
    if (!user) return "?";
    let initials = "";
    if (user.first_name) initials += user.first_name.charAt(0).toUpperCase();
    if (user.last_name) initials += user.last_name.charAt(0).toUpperCase();
    return initials || user.email?.charAt(0).toUpperCase() || "?";
  };

  const getChatbotVisual = (type = "any") => {
    if (!chatbot) return null;
    if (chatbot.logo && (type === "logo" || type === "any"))
      return chatbot.logo;
    if (chatbot.icon && (type === "icon" || type === "any"))
      return chatbot.icon;
    return null;
  };

  const BOTTOM_SNAP_THRESHOLD_PX = 20;

  const isNearBottom = useCallback((container) => {
    if (!container) return true;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= BOTTOM_SNAP_THRESHOLD_PX;
  }, []);

  const syncAutoScrollState = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    shouldAutoScrollRef.current = isNearBottom(container);
  }, [isNearBottom]);

  const scrollToBottom = useCallback(
    (instant = false, force = false) => {
      const container = messagesContainerRef.current;
      if (!container) return;

      if (!force && !shouldAutoScrollRef.current) {
        return;
      }

      if (autoScrollRafRef.current) {
        cancelAnimationFrame(autoScrollRafRef.current);
      }

      autoScrollRafRef.current = requestAnimationFrame(() => {
        const targetTop = container.scrollHeight - container.clientHeight;
        const delta = Math.abs(targetTop - container.scrollTop);

        // Avoid unnecessary micro-scroll commands that can cause visual jitter.
        if (delta < 1) return;

        const shouldSnap = instant || delta <= 60;
        container.scrollTo({
          top: targetTop,
          behavior: shouldSnap ? "auto" : "smooth",
        });
        lastAutoScrollTopRef.current = targetTop;
      });
    },
    []
  );

  // Track previous message count to detect new messages vs history loads
  const prevMessageCountRef = useRef(0);

  // Use layoutEffect for initial/history loads to prevent visible jumps.
  useLayoutEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;

    if (currentCount === 0) {
      prevMessageCountRef.current = 0;
      return;
    }

    // Check if we're loading older messages or adjusting scroll - don't interfere
    if (loadingOlderMessagesRef.current || adjustingScrollRef.current) {
      prevMessageCountRef.current = currentCount;
      return;
    }

    // If this is the first load of messages from history, scroll instantly to bottom
    // Using layoutEffect ensures this happens before paint, preventing visible jump
    if (prevCount === 0 && currentCount > 0 && messages[0]?.fromHistory) {
      scrollToBottom(true, true);
      prevMessageCountRef.current = currentCount;
      shouldAutoScrollRef.current = true;
      return;
    }

    prevMessageCountRef.current = currentCount;
  }, [messages, scrollToBottom]);

  // Follow the stream smoothly only when the user is already at the bottom.
  useEffect(() => {
    if (
      messages.length === 0 ||
      loadingOlderMessagesRef.current ||
      adjustingScrollRef.current
    ) {
      return;
    }

    // Only follow on live messages (never on history hydration/pagination items).
    if (!messages[messages.length - 1]?.fromHistory) {
      scrollToBottom(false, false);
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (autoScrollRafRef.current) {
        cancelAnimationFrame(autoScrollRafRef.current);
      }
    };
  }, []);

  // Watch for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(currentTheme === 'dark');
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!chatbotId) {
      if (
        wsInstance.current &&
        wsInstance.current.readyState === WebSocket.OPEN
      ) {
        wsInstance.current.close(1000, "Chatbot ID cleared");
      }
      setMessages([]);
      lastSeenSeqRef.current = 0;
      setHasMoreMessages(false);
      setTotalMessageCount(0);
      setChatbot(null);
      setIsConnected(false);
      setIsConnecting(false);
      setChatError(null);
      setHistoryCards([]);
      setHasMoreHistory(false);
      setCurrentHistoryId(null);
      return;
    }

    // Wait for chatbot data to be loaded before connecting WebSocket
    if (!chatbot || loadingChatbot) {
      return;
    }

    if (prevChatbotIdRef.current !== chatbotId && wsInstance.current) {
      wsInstance.current.onclose = null;
      wsInstance.current.close(1000, "Chatbot changed");
    }
    prevChatbotIdRef.current = chatbotId;

    // Only clear messages if we're not loading a specific history from search
    // If coming from search, the history loading effect will set the messages
    if (!historyIdParam) {
      setMessages([]);
      lastSeenSeqRef.current = 0;
      setHasMoreMessages(false);
      setTotalMessageCount(0);
    }
    setChatError(null);
    setIsConnecting(true);
    setIsConnected(false);
    currentBotMessageId.current = null;
    responseSegmentStateRef.current = {};
    activeFunctionMessageByResponseRef.current = {};
    // setIsTyping(false); // Display removed

    const ws = new WebSocket(getWebSocketURL(chatbot));
    wsInstance.current = ws;

    ws.onopen = () => {
      setIsConnecting(false);
      setIsConnected(true);
      setChatError(null);
      const initPayload = { type: "init", chatbot_id: chatbotId };
      
      // Check if we have a history ID from the current ref or from URL (search)
      if (currentHistoryIdRef.current) {
        initPayload.chat_history_id = currentHistoryIdRef.current;
      } else if (historyIdParam) {
        // If coming from search, use the history ID from URL
        initPayload.chat_history_id = historyIdParam;
      }

      initPayload.last_seen_seq = lastSeenSeqRef.current || 0;
      ws.send(JSON.stringify(initPayload));
    };

    ws.onmessage = (event) => {
      let serverMessage;
      try {
        serverMessage = JSON.parse(event.data);
      } catch (e) {
        setChatError("Received an invalid message from the server.");
        return;
      }

      // Track the highest seq we've seen so reconnect can ask the server to
      // replay only events past it. Do NOT use seq to filter — chat_runs.seq
      // resets to 0 on every new run, so a stale "max seen" from a prior run
      // would silently swallow the next run's events. The server's
      // _attach_to_active_run already dedupes replay vs. live for us.
      if (typeof serverMessage?.seq === "number"
          && serverMessage.seq > lastSeenSeqRef.current) {
        lastSeenSeqRef.current = serverMessage.seq;
      }
      // Reset the per-run seq tracker on run boundaries so reconnect reports
      // 0 (matching the next run's fresh counter).
      if (serverMessage?.type === "run_completed"
          || serverMessage?.type === "run_failed"
          || serverMessage?.type === "run_cancelled") {
        lastSeenSeqRef.current = 0;
      }

      // Debug logging for web search events
      if (
        serverMessage.type === "web_search_started" ||
        serverMessage.type === "web_search_completed" ||
        serverMessage.type === "image_generation_started" ||
        serverMessage.type === "image_generation_completed"
      ) {
      }

      switch (serverMessage.type) {
        case "preparing": {
          // Server is uploading files or spinning up a code interpreter container.
          // Show a loading-dots assistant bubble with a status label so the user
          // knows something is happening before the actual response starts.
          const preparingId = "preparing_status";
          setIsResponding(true);
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === preparingId);
            if (exists) {
              return prev.map((m) =>
                m.id === preparingId
                  ? { ...m, preparingMessage: serverMessage.message }
                  : m
              );
            }
            return [
              ...prev,
              {
                id: preparingId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: true,
                preparingMessage: serverMessage.message || "Preparing...",
              },
            ];
          });
          break;
        }

        case "run_resumed":
          setIsResponding(true);
          shouldAutoScrollRef.current = false;
          break;

        case "run_completed":
        case "run_failed":
        case "run_cancelled":
          setIsResponding(false);
          shouldAutoScrollRef.current = true;
          requestAnimationFrame(() => {
            const c = messagesContainerRef.current;
            if (c) c.scrollTop = c.scrollHeight;
          });
          break;

        case "function_call_started":
          // For multi-step tool use (e.g. Anthropic), each tool call is its own message bubble.
          // Any subsequent text after this call should start in a new assistant bubble.
          const functionStartResponseId =
            serverMessage.response_id || currentBotMessageId.current;
          if (!functionStartResponseId) break;
          const functionMessageId = `${functionStartResponseId}_fn_${
            serverMessage.call_id || Date.now()
          }`;
          const existingSegmentState =
            responseSegmentStateRef.current[functionStartResponseId] || {};
          responseSegmentStateRef.current[functionStartResponseId] = {
            hasFunctionCalls: true,
            activeTextMessageId: null,
            textPartCounter: existingSegmentState.textPartCounter || 0,
          };
          activeFunctionMessageByResponseRef.current[functionStartResponseId] =
            functionMessageId;

          setCurrentFunctionCall({
            name: serverMessage.function_name || "Function",
            callId: serverMessage.call_id,
            responseId: functionStartResponseId,
            messageId: functionMessageId,
            active: true,
          });
          setMessages((prev) => {
            const placeholderMessage = prev.find(
              (m) =>
                (m.id === functionStartResponseId ||
                  m.id === `${functionStartResponseId}_response`) &&
                !m.content &&
                !m.webSearchStatus &&
                !m.imageGenerationStatus &&
                !m.codeInterpreterStatus &&
                !m.functionCallStatus
            );

            if (placeholderMessage) {
              currentBotMessageId.current = placeholderMessage.id;
              return prev.map((m) =>
                m.id === placeholderMessage.id
                  ? {
                      ...m,
                      id: functionMessageId,
                      role: "assistant",
                      content: "",
                      timestamp: m.timestamp || new Date().toISOString(),
                      isTypingPlaceholder: false,
                      functionCallStatus: "started",
                      functionName: serverMessage.function_name,
                    }
                  : m
              );
            }

            const cleaned = prev.filter(
              (m) =>
                !(
                  (m.id === functionStartResponseId ||
                    m.id === `${functionStartResponseId}_response`) &&
                  m.isTypingPlaceholder &&
                  !m.content
                )
            );

            if (cleaned.some((m) => m.id === functionMessageId)) {
              currentBotMessageId.current = functionMessageId;
              return cleaned.map((m) =>
                m.id === functionMessageId
                  ? {
                      ...m,
                      isTypingPlaceholder: false,
                      functionCallStatus: "started",
                      functionName: serverMessage.function_name,
                    }
                  : m
              );
            }
            currentBotMessageId.current = functionMessageId;
            return [
              ...cleaned,
              {
                id: functionMessageId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false,
                functionCallStatus: "started",
                functionName: serverMessage.function_name,
              },
            ];
          });
          break;

        case "function_call_completed":
          setCurrentFunctionCall(null);
          const functionCompleteResponseId =
            serverMessage.response_id || currentBotMessageId.current;
          if (functionCompleteResponseId) {
            const priorState =
              responseSegmentStateRef.current[functionCompleteResponseId] || {};
            responseSegmentStateRef.current[functionCompleteResponseId] = {
              ...priorState,
              hasFunctionCalls: true,
              activeTextMessageId: null,
              textPartCounter: priorState.textPartCounter || 0,
            };
          }
          setMessages((prev) =>
            prev.map((m) => {
              const resolvedFunctionMessageId = functionCompleteResponseId
                ? activeFunctionMessageByResponseRef.current[
                    functionCompleteResponseId
                  ] || currentBotMessageId.current
                : currentBotMessageId.current;
              if (m.id === resolvedFunctionMessageId) {
                return {
                  ...m,
                  functionCallStatus: "completed",
                  isTypingPlaceholder: false,
                };
              }
              if (
                functionCompleteResponseId &&
                m.id === functionCompleteResponseId &&
                m.isTypingPlaceholder
              ) {
                return { ...m, isTypingPlaceholder: false };
              }
              return m;
            })
          );
          break;

        case "code_interpreter_started":
          setCurrentCodeInterpreter({
            callId: serverMessage.call_id,
            responseId: serverMessage.response_id,
            active: true,
          });
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            const messageExists = prev.some((m) => m.id === responseId);

            if (messageExists) {
              return prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false,
                      codeInterpreterStatus: "started",
                    }
                  : m
              );
            } else {
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false,
                codeInterpreterStatus: "started",
              };
              return [...prev, newMessage];
            }
          });
          if (!currentBotMessageId.current && serverMessage.response_id) {
            currentBotMessageId.current = serverMessage.response_id;
          }
          break;

        case "code_interpreter_completed":
          setCurrentCodeInterpreter(null);
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            // Update message to completed
            const updated = prev.map((m) =>
              m.id === responseId
                ? {
                    ...m,
                    codeInterpreterStatus: "completed",
                    isTypingPlaceholder: true, // Resume typing/loading if text follows
                  }
                : m
            );
            return updated;
          });
          break;

        case "web_search_started":
          // Show web search status similar to function calls
          setCurrentWebSearch({
            callId: serverMessage.web_search_call_id,
            responseId: serverMessage.response_id,
            action: serverMessage.action,
            active: true,
          });
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              return prev;
            }

            // Check if message already exists
            const messageExists = prev.some((m) => m.id === responseId);
            if (messageExists) {
              // Update existing message - force web search status to show
              const updated = prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false, // Force false to show web search status
                      webSearchStatus: "started",
                      webSearchAction: serverMessage.action,
                    }
                  : m
              );
              return updated;
            } else {
              // Create new message if it doesn't exist yet
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false, // Don't show loading dots, show web search status
                webSearchStatus: "started",
                webSearchAction: serverMessage.action,
              };
              return [...prev, newMessage];
            }
          });
          // Update currentBotMessageId if it wasn't set
          if (!currentBotMessageId.current && serverMessage.response_id) {
            currentBotMessageId.current = serverMessage.response_id;
          }
          break;

        case "web_search_completed":
          setCurrentWebSearch(null);
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            // Update web search message to completed
            const updated = prev.map((m) =>
              m.id === responseId
                ? {
                    ...m,
                    webSearchStatus: "completed",
                    webSearchAction:
                      serverMessage.action || m.webSearchAction || "search",
                    webSearchQuery:
                      serverMessage.query || m.webSearchQuery || null,
                    webSearchDomains:
                      serverMessage.domains || m.webSearchDomains || null,
                    webSearchSources: Array.isArray(serverMessage.sources)
                      ? serverMessage.sources
                      : m.webSearchSources,
                  }
                : m
            );

            // Create a new message for the chatbot's response with loading dots
            const newResponseId = `${responseId}_response`;
            const responseMessageExists = updated.some(
              (m) => m.id === newResponseId
            );

            if (!responseMessageExists) {
              // Create new response message with loading dots
              currentBotMessageId.current = newResponseId;
              return [
                ...updated,
                {
                  id: newResponseId,
                  role: "assistant",
                  content: "",
                  timestamp: new Date().toISOString(),
                  isTypingPlaceholder: true, // Show loading dots
                },
              ];
            }

            return updated;
          });
          break;

        case "image_generation_started":
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              return prev;
            }

            // Check if message already exists
            const messageExists = prev.some((m) => m.id === responseId);

            if (messageExists) {
              // Update existing message
              return prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false,
                      imageGenerationStatus: "started",
                    }
                  : m
              );
            } else {
              // Create new message
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false,
                imageGenerationStatus: "started",
              };
              return [...prev, newMessage];
            }
          });
          break;

        case "image_generation_completed":
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              return prev;
            }

            // Update message with generated image
            const updated = prev.map((m) => {
              if (m.id === responseId) {
                let imageUrl = null;

                // Check if we have image_path (filename)
                if (serverMessage.image_path) {
                  // Construct URL to fetch image from server
                  imageUrl = `${config.api.baseUrl}${
                    config.api.endpoints.chatbot.base
                  }/files/${encodeURIComponent(serverMessage.image_path)}`;
                }

                const updatedMessage = {
                  ...m,
                  imageGenerationStatus: "completed",
                  generatedImage: imageUrl, // Store URL instead of data URL
                  revisedPrompt: serverMessage.revised_prompt,
                  isTypingPlaceholder: false, // Explicitly clear loading dots
                };

                return updatedMessage;
              }
              return m;
            });

            return updated;
          });
          break;

        case "init_success":
          setChatError(null);
          break;
        case "history_opened":
          if (serverMessage.chat_history_id) {
            // Stale-message guard. history_opened comes from the server in
            // response to (a) the initial WS init, (b) a mid-connection init
            // when the user switches histories, or (c) the server creating a
            // new history out of the user's first message. If the user has
            // navigated away since the message that triggered (a) or (b) was
            // sent — e.g. they clicked + New Chat right after a refresh — a
            // late-arriving history_opened would otherwise yank them back to
            // the chat they just left.
            //
            // We use window.location.search (browser-truth, no React state
            // lag) plus currentHistoryIdRef to detect that:
            //   - If the URL has a historyId different from this message,
            //     the user switched to a different history → drop.
            //   - If the URL has NO historyId but currentHistoryIdRef is set,
            //     the user explicitly went to a new-chat view → drop. (The
            //     legitimate new-chat-from-message case (c) has
            //     currentHistoryIdRef === null, so it still passes.)
            const urlHistoryId = new URLSearchParams(window.location.search).get("historyId");
            if (urlHistoryId !== null && urlHistoryId !== serverMessage.chat_history_id) {
              break;
            }
            if (urlHistoryId === null && currentHistoryIdRef.current !== null) {
              break;
            }
            setCurrentHistoryId(serverMessage.chat_history_id);
            currentHistoryIdRef.current = serverMessage.chat_history_id;
            if (serverMessage.selected_model) {
              setSelectedModel(serverMessage.selected_model);
            }
            if (serverMessage.selected_provider) {
              setSelectedModelProvider(
                String(serverMessage.selected_provider).toLowerCase()
              );
            }
            setProviderLock(
              serverMessage.function_provider_lock
                ? String(serverMessage.function_provider_lock).toLowerCase()
                : null
            );
            if (serverMessage.title) {
              // If the server provides initial title like "New Chat", add/update the card optimistically
              setHistoryCards((prev) => {
                const exists = prev.some(
                  (c) => c._id === serverMessage.chat_history_id
                );
                // Use current UTC timestamp; formatting logic handles display
                const updatedAt = new Date().toISOString();
                const newCard = {
                  _id: serverMessage.chat_history_id,
                  title: serverMessage.title,
                  created_at: updatedAt,
                  updated_at: updatedAt,
                  fromLocal: true,
                  message_count: 1,
                };
                return exists
                  ? prev.map((c) =>
                      c._id === newCard._id ? { ...c, title: newCard.title } : c
                    )
                  : [newCard, ...prev];
              });
            }
          }
          break;
        case "history_title_updated":
          if (serverMessage.chat_history_id && serverMessage.title) {
            setHistoryCards((prev) =>
              prev.map((c) =>
                c._id === serverMessage.chat_history_id
                  ? {
                      ...c,
                      title: serverMessage.title,
                      updated_at: new Date().toISOString(),
                    }
                  : c
              )
            );
          }
          break;

        case "response_created": {
          const isAgentMode = serverMessage.agent_mode === true;
          const agentFields = isAgentMode ? { agentMode: true, agentActivity: [] } : {};
          currentBotMessageId.current = serverMessage.response_id;
          responseSegmentStateRef.current[serverMessage.response_id] = {
            hasFunctionCalls: false,
            activeTextMessageId: serverMessage.response_id,
            textPartCounter: 0,
          };
          setIsResponding(true); // Mark that chatbot is responding
          setMessages((prevMessages) => {
            // Remove the "preparing" status bubble if present
            // Remove the "preparing" status bubble if present
            const withoutPreparing = prevMessages.filter((m) => m.id !== "preparing_status");
            // Check if message already exists (e.g., from web_search_started)
            const existingMessage = withoutPreparing.find(
              (m) => m.id === serverMessage.response_id
            );
            if (existingMessage) {
              // If the existing message has web search status, don't update it
              // Create a new message for the actual response instead
              if (
                existingMessage.webSearchStatus === "started" ||
                existingMessage.webSearchStatus === "completed"
              ) {
                // Create a new message for the chatbot's response
                // Use a modified ID to ensure it's separate
                const newResponseId = `${serverMessage.response_id}_response`;
                currentBotMessageId.current = newResponseId;
                responseSegmentStateRef.current[serverMessage.response_id] = {
                  hasFunctionCalls: false,
                  activeTextMessageId: newResponseId,
                  textPartCounter: 0,
                };
                return [
                  ...withoutPreparing,
                  {
                    id: newResponseId,
                    role: "assistant",
                    content: "",
                    timestamp: new Date().toISOString(),
                    isTypingPlaceholder: true,
                    ...agentFields,
                  },
                ];
              } else if (
                existingMessage.imageGenerationStatus === "completed"
              ) {
                // If image generation is already completed, create a separate message for text response
                const newResponseId = `${serverMessage.response_id}_response`;
                currentBotMessageId.current = newResponseId;
                responseSegmentStateRef.current[serverMessage.response_id] = {
                  hasFunctionCalls: false,
                  activeTextMessageId: newResponseId,
                  textPartCounter: 0,
                };
                return [
                  ...withoutPreparing,
                  {
                    id: newResponseId,
                    role: "assistant",
                    content: "",
                    timestamp: new Date().toISOString(),
                    isTypingPlaceholder: true,
                    ...agentFields,
                  },
                ];
              } else {
                // Update existing message if it doesn't have web search or image generation status
                return withoutPreparing.map((m) =>
                  m.id === serverMessage.response_id
                    ? {
                        ...m,
                        role: "assistant",
                        timestamp: m.timestamp || new Date().toISOString(),
                        isTypingPlaceholder: true,
                        ...agentFields,
                      }
                    : m
                );
              }
            } else {
              // Create new message
              return [
                ...withoutPreparing,
                {
                  id: serverMessage.response_id,
                  role: "assistant",
                  content: "",
                  timestamp: new Date().toISOString(),
                  isTypingPlaceholder: true,
                  ...agentFields,
                },
              ];
            }
          });
          break;
        }

        case "text_delta":
          if (serverMessage.delta) {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) break;

            setMessages((prevMessages) => {
              // Agent mode: store text in agentActivity for inline chronological rendering
              const agentMsg = prevMessages.find((m) => m.id === responseId && m.agentMode);
              if (agentMsg) {
                return prevMessages.map((m) => {
                  if (m.id !== responseId) return m;
                  const activity = [...(m.agentActivity || [])];
                  const lastItem = activity[activity.length - 1];
                  if (lastItem && lastItem.type === "text") {
                    // Append to existing text block
                    activity[activity.length - 1] = {
                      ...lastItem,
                      content: lastItem.content + serverMessage.delta,
                    };
                  } else {
                    // Start a new text block
                    activity.push({
                      type: "text",
                      content: serverMessage.delta,
                      timestamp: Date.now(),
                    });
                  }
                  return {
                    ...m,
                    agentActivity: activity,
                    content: (m.content || "") + serverMessage.delta,
                    isTypingPlaceholder: false,
                  };
                });
              }

              const segmentState = responseSegmentStateRef.current[responseId];
              if (segmentState?.hasFunctionCalls) {
                const withoutStaleTypingPlaceholder = prevMessages.filter(
                  (msg) =>
                    !(
                      (msg.id === responseId ||
                        msg.id === `${responseId}_response`) &&
                      msg.isTypingPlaceholder &&
                      !msg.content
                    )
                );
                const activeTextMessageId = segmentState.activeTextMessageId;
                if (activeTextMessageId) {
                  currentBotMessageId.current = activeTextMessageId;
                  return withoutStaleTypingPlaceholder.map((msg) =>
                    msg.id === activeTextMessageId
                      ? {
                          ...msg,
                          content: (msg.content || "") + serverMessage.delta,
                          isTypingPlaceholder: false,
                        }
                      : msg
                  );
                }

                const nextPart = (segmentState.textPartCounter || 0) + 1;
                const nextTextMessageId = `${responseId}_text_${nextPart}`;
                responseSegmentStateRef.current[responseId] = {
                  ...segmentState,
                  activeTextMessageId: nextTextMessageId,
                  textPartCounter: nextPart,
                };
                currentBotMessageId.current = nextTextMessageId;
                return [
                  ...withoutStaleTypingPlaceholder,
                  {
                    id: nextTextMessageId,
                    role: "assistant",
                    content: serverMessage.delta,
                    timestamp: new Date().toISOString(),
                    isTypingPlaceholder: false,
                  },
                ];
              }

              // Find the message with this response_id
              const messageWithResponseId = prevMessages.find(
                (m) => m.id === responseId
              );

              // If the message has image generation completed, create/update a separate response message for text
              if (
                messageWithResponseId &&
                messageWithResponseId.imageGenerationStatus === "completed"
              ) {
                // Find or create the response message (separate from image generation message)
                const newResponseId = `${responseId}_response`;
                const responseMessage = prevMessages.find(
                  (m) => m.id === newResponseId
                );

                if (responseMessage) {
                  // Update the existing response message
                  currentBotMessageId.current = newResponseId;
                  return prevMessages.map((msg) =>
                    msg.id === newResponseId
                      ? {
                          ...msg,
                          content: (msg.content || "") + serverMessage.delta,
                          isTypingPlaceholder: false,
                        }
                      : msg
                  );
                } else {
                  // Create new response message for the chatbot's text commentary
                  currentBotMessageId.current = newResponseId;
                  return [
                    ...prevMessages,
                    {
                      id: newResponseId,
                      role: "assistant",
                      content: serverMessage.delta,
                      timestamp: new Date().toISOString(),
                      isTypingPlaceholder: false,
                    },
                  ];
                }
              }

              // If the message has web search status, create/update a separate response message
              if (
                messageWithResponseId &&
                (messageWithResponseId.webSearchStatus === "started" ||
                  messageWithResponseId.webSearchStatus === "completed")
              ) {
                // Find or create the response message (separate from web search message)
                const newResponseId = `${responseId}_response`;
                const responseMessage = prevMessages.find(
                  (m) => m.id === newResponseId
                );

                if (responseMessage) {
                  // Update the existing response message
                  currentBotMessageId.current = newResponseId;
                  return prevMessages.map((msg) =>
                    msg.id === newResponseId
                      ? {
                          ...msg,
                          content: (msg.content || "") + serverMessage.delta,
                          isTypingPlaceholder: false,
                        }
                      : msg
                  );
                } else {
                  // Create new response message for the chatbot's actual response
                  currentBotMessageId.current = newResponseId;
                  return [
                    ...prevMessages,
                    {
                      id: newResponseId,
                      role: "assistant",
                      content: serverMessage.delta,
                      timestamp: new Date().toISOString(),
                      isTypingPlaceholder: false,
                    },
                  ];
                }
              } else {
                // Normal update - no web search status, update the message directly
                if (!currentBotMessageId.current) {
                  currentBotMessageId.current = responseId;
                }
                return prevMessages.map((msg) =>
                  msg.id === responseId
                    ? {
                        ...msg,
                        content: (msg.content || "") + serverMessage.delta,
                        isTypingPlaceholder: false,
                      }
                    : msg
                );
              }
            });
          }
          break;

        case "content_update":
          // Update message content when backend replaces links (e.g., /mnt/data -> /chatbot/files)
          const updateResponseId =
            serverMessage.response_id || currentBotMessageId.current;
          if (updateResponseId && serverMessage.content) {
            setMessages((prevMessages) => {
              // Check if this is a split response (e.g., for web search or image generation)
              const splitResponseId = `${updateResponseId}_response`;
              const hasSplitResponse = prevMessages.some(
                (m) => m.id === splitResponseId
              );

              return prevMessages.map((msg) => {
                // Update the split response if it exists, otherwise update the main message
                const targetId = hasSplitResponse
                  ? splitResponseId
                  : updateResponseId;
                return msg.id === targetId
                  ? {
                      ...msg,
                      content: serverMessage.content,
                    }
                  : msg;
              });
            });
          }
          break;

        case "response_completed":
          const responseId = serverMessage.response_id;
          setIsResponding(false); // Response completed
          setMessages((prevMessages) => {
            // Ensure preparing bubble is gone
            prevMessages = prevMessages.filter((m) => m.id !== "preparing_status");
            const segmentState = responseSegmentStateRef.current[responseId];
            if (segmentState?.hasFunctionCalls) {
              const activeTextMessageId = segmentState.activeTextMessageId;
              currentBotMessageId.current = null;
              delete responseSegmentStateRef.current[responseId];
              delete activeFunctionMessageByResponseRef.current[responseId];
              if (!activeTextMessageId) {
                return prevMessages;
              }
              return prevMessages.map((msg) =>
                msg.id === activeTextMessageId
                  ? {
                      ...msg,
                      isTypingPlaceholder: false,
                    }
                  : msg
              );
            }

            // Check if there's a web search message with this response_id
            const webSearchMessage = prevMessages.find(
              (m) =>
                m.id === responseId &&
                (m.webSearchStatus === "started" ||
                  m.webSearchStatus === "completed")
            );

            if (webSearchMessage) {
              // If there's a web search message, check for a separate response message
              const newResponseId = `${responseId}_response`;
              const responseMessage = prevMessages.find(
                (m) => m.id === newResponseId
              );

              if (responseMessage) {
                // Update the response message (not the web search message)
                currentBotMessageId.current = null;
                delete responseSegmentStateRef.current[responseId];
                return prevMessages.map((msg) =>
                  msg.id === newResponseId
                    ? {
                        ...msg,
                        isTypingPlaceholder: false,
                        content: serverMessage.final_content || msg.content,
                        webSearchSources:
                          msg.webSearchSources ||
                          webSearchMessage.webSearchSources ||
                          undefined,
                      }
                    : msg
                );
              } else {
                // No response message yet, just clear typing on web search message if needed
                currentBotMessageId.current = null;
                delete responseSegmentStateRef.current[responseId];
                return prevMessages;
              }
            } else {
              // Normal completion - no web search message
              currentBotMessageId.current = null;
              delete responseSegmentStateRef.current[responseId];
              return prevMessages.map((msg) =>
                msg.id === responseId
                  ? {
                      ...msg,
                      isTypingPlaceholder: false,
                      content: serverMessage.final_content || msg.content,
                      webSearchSources:
                        msg.webSearchSources ||
                        (Array.isArray(serverMessage.sources)
                          ? serverMessage.sources
                          : undefined),
                    }
                  : msg
              );
            }
          });
          // Optimistically increment message_count for active history on assistant completion
          if (currentHistoryIdRef.current) {
            const nowIso = new Date().toISOString();
            setHistoryCards((prev) => {
              const cards = Array.isArray(prev) ? [...prev] : [];
              const idx = cards.findIndex(
                (c) => c._id === currentHistoryIdRef.current
              );
              if (idx === -1) return prev;
              const card = cards[idx];
              const updatedCard = {
                ...card,
                updated_at: nowIso,
                fromLocal: true,
                message_count: (card.message_count || 0) + 1,
              };
              cards.splice(idx, 1);
              cards.unshift(updatedCard);
              return cards;
            });
          }
          scrollToBottom();
          break;

        case "agent_thinking": {
          // Complete thinking block (non-streaming fallback)
          const thinkResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!thinkResponseId) break;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === thinkResponseId
                ? {
                    ...m,
                    isTypingPlaceholder: false,
                    agentActivity: [
                      ...(m.agentActivity || []),
                      {
                        type: "thinking",
                        thinking: serverMessage.thinking,
                        timestamp: Date.now(),
                      },
                    ],
                  }
                : m
            )
          );
          break;
        }

        case "agent_thinking_start": {
          // Start a new streaming thinking block
          const tsResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!tsResponseId) break;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tsResponseId
                ? {
                    ...m,
                    isTypingPlaceholder: false,
                    agentActivity: [
                      ...(m.agentActivity || []),
                      {
                        type: "thinking",
                        thinking: "",
                        streaming: true,
                        timestamp: Date.now(),
                      },
                    ],
                  }
                : m
            )
          );
          break;
        }

        case "agent_thinking_delta": {
          // Append to the current streaming thinking block
          const tdResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!tdResponseId) break;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== tdResponseId) return m;
              const activity = [...(m.agentActivity || [])];
              // Find the last streaming thinking block
              for (let i = activity.length - 1; i >= 0; i--) {
                if (activity[i].type === "thinking" && activity[i].streaming) {
                  activity[i] = {
                    ...activity[i],
                    thinking: activity[i].thinking + serverMessage.delta,
                  };
                  break;
                }
              }
              return { ...m, agentActivity: activity };
            })
          );
          break;
        }

        case "agent_thinking_stop": {
          // Finalize the streaming thinking block
          const tstoResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!tstoResponseId) break;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== tstoResponseId) return m;
              const activity = [...(m.agentActivity || [])];
              for (let i = activity.length - 1; i >= 0; i--) {
                if (activity[i].type === "thinking" && activity[i].streaming) {
                  activity[i] = {
                    ...activity[i],
                    streaming: false,
                    thinking: serverMessage.thinking || activity[i].thinking,
                  };
                  break;
                }
              }
              return { ...m, agentActivity: activity };
            })
          );
          break;
        }

        case "agent_tool_use": {
          const toolResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!toolResponseId) break;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === toolResponseId
                ? {
                    ...m,
                    isTypingPlaceholder: false,
                    agentActivity: [
                      ...(m.agentActivity || []),
                      {
                        type: "tool_use",
                        tool_id: serverMessage.tool_id,
                        tool_name: serverMessage.tool_name,
                        tool_input: serverMessage.tool_input,
                        status: "pending",
                        timestamp: Date.now(),
                      },
                    ],
                  }
                : m
            )
          );
          break;
        }

        case "agent_tool_use_complete": {
          // Update existing tool_use entry with final parsed input
          const tucResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!tucResponseId) break;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== tucResponseId) return m;
              const activity = (m.agentActivity || []).map((event) => {
                if (
                  event.type === "tool_use" &&
                  event.tool_id === serverMessage.tool_id
                ) {
                  return {
                    ...event,
                    tool_input: serverMessage.tool_input,
                  };
                }
                return event;
              });
              return { ...m, agentActivity: activity };
            })
          );
          break;
        }

        case "agent_tool_result": {
          const resultResponseId = serverMessage.response_id || currentBotMessageId.current;
          if (!resultResponseId) break;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== resultResponseId) return m;
              const updatedActivity = (m.agentActivity || []).map((event) => {
                if (
                  event.type === "tool_use" &&
                  event.tool_id === serverMessage.tool_use_id &&
                  event.status === "pending"
                ) {
                  return {
                    ...event,
                    status: serverMessage.is_error ? "error" : "completed",
                    result_content: serverMessage.content,
                    is_error: serverMessage.is_error,
                  };
                }
                return event;
              });
              return { ...m, agentActivity: updatedActivity };
            })
          );
          break;
        }

        case "error":
          setChatError(
            serverMessage.message || "An error occurred with the chat server."
          );
          setIsConnected(ws.readyState === WebSocket.OPEN);
          setIsResponding(false); // Stop responding on error
          setMessages((prev) => prev.filter((m) => m.id !== "preparing_status"));
          break;

        case "response_stopped":
          setIsResponding(false); // Stop responding
          // Remove loading dots from any message with this response_id
          const stoppedResponseId = serverMessage.response_id;
          setMessages((prevMessages) => {
            const segmentState = responseSegmentStateRef.current[stoppedResponseId];
            if (segmentState?.hasFunctionCalls) {
              const activeTextMessageId = segmentState.activeTextMessageId;
              return prevMessages.map((msg) => {
                if (activeTextMessageId && msg.id === activeTextMessageId && msg.isTypingPlaceholder) {
                  return { ...msg, isTypingPlaceholder: false };
                }
                return msg;
              });
            }
            return prevMessages.map((msg) => {
              // Check if this message matches the response_id or is a response message
              if (
                msg.id === stoppedResponseId ||
                msg.id === `${stoppedResponseId}_response` ||
                (currentBotMessageId.current &&
                  msg.id === currentBotMessageId.current)
              ) {
                // Remove typing placeholder if it exists
                if (msg.isTypingPlaceholder) {
                  return {
                    ...msg,
                    isTypingPlaceholder: false,
                  };
                }
              }
              return msg;
            });
          });
          currentBotMessageId.current = null;
          if (stoppedResponseId) {
            delete responseSegmentStateRef.current[stoppedResponseId];
            delete activeFunctionMessageByResponseRef.current[stoppedResponseId];
          }
          break;

        case "ping":
          break;

        default:
          break;
      }
    };

    ws.onerror = (errorEvent) => {
      setIsConnecting(false);
      setIsConnected(false);
      setChatError("WebSocket connection error. Please try refreshing.");
    };

    ws.onclose = (event) => {
      setIsConnecting(false);
      setIsConnected(false);
      currentBotMessageId.current = null;
      responseSegmentStateRef.current = {};
      activeFunctionMessageByResponseRef.current = {};
      if (!event.wasClean && event.code !== 1000) {
        setChatError("Disconnected from chat. Please refresh to reconnect.");
      }
    };

    return () => {
      if (wsInstance.current) {
        wsInstance.current.onclose = null;
        wsInstance.current.close(
          1000,
          "Component unmounting or chatbotId changing"
        );
        wsInstance.current = null;
      }
      setIsConnected(false);
      setIsConnecting(false);
    };
    // NOTE: historyIdParam is intentionally NOT a dependency. Switching between
    // chat histories on the same chatbot is handled by sending a mid-connection
    // `init` message over the existing WS (see handleSelectHistoryCard). If we
    // re-ran this effect on every URL change, every history click would tear
    // down and rebuild the WS, which (a) loses in-flight bus subscriptions
    // mid-stream, and (b) fights with the mid-init flow — the symptom is the
    // UI ping-ponging between two histories while a response is streaming.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId, chatbot, loadingChatbot, scrollToBottom]);

  useEffect(() => {
    if (!chatbotId) {
      setLoadingChatbot(false);
      setFetchError("No chatbot selected.");
      setChatbot(null);
      return;
    }
    setLoadingChatbot(true);
    setFetchError(null);

    const fetchChatbotDetails = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/${chatbotId}`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success) {
          setChatbot(data.chatbot);
        } else {
          throw new Error(data.error || "Failed to fetch chatbot details");
        }
      } catch (err) {
        setFetchError(err.message || "Failed to load chatbot details.");
        setChatbot(null);
      } finally {
        setLoadingChatbot(false);
      }
    };
    fetchChatbotDetails();
  }, [chatbotId]);

  // Load chat history cards for this chatbot
  useEffect(() => {
    const fetchHistoryCards = async () => {
      if (!chatbotId) {
        setHistoryCards([]);
        setHasMoreHistory(false);
        return;
      }
      setLoadingHistoryCards(true);
      setHistoryError(null);
      try {
        const url = `${config.api.baseUrl}${
          config.api.endpoints.chatbot.base
        }/history/cards?chatbot_id=${encodeURIComponent(chatbotId)}&limit=50&skip=0`;
        const res = await fetch(url, { credentials: "include" });
        const data = await res.json();
        if (data.success && Array.isArray(data.cards)) {
          setHistoryCards(data.cards);
          setHasMoreHistory(data.has_more || false);
        } else {
          throw new Error(data.error || "Failed to load history.");
        }
      } catch (e) {
        setHistoryError(e.message || "Failed to load history.");
        setHistoryCards([]);
        setHasMoreHistory(false);
      } finally {
        setLoadingHistoryCards(false);
      }
    };
    fetchHistoryCards();
  }, [chatbotId]);

  // Function to load more history cards (for infinite scroll)
  const loadMoreHistoryCards = useCallback(async () => {
    // Use ref for immediate check to prevent race conditions
    if (!chatbotId || loadingMoreHistoryRef.current || !hasMoreHistory) {
      return;
    }
    
    // Add cooldown: don't load if we just loaded within the last 500ms
    const now = Date.now();
    if (now - lastLoadTimeRef.current < 500) {
      return;
    }
    
    // Set both ref (immediate) and state (for UI)
    loadingMoreHistoryRef.current = true;
    setLoadingMoreHistory(true);
    lastLoadTimeRef.current = now;
    
    try {
      const skip = historyCards.length;
      const url = `${config.api.baseUrl}${
        config.api.endpoints.chatbot.base
      }/history/cards?chatbot_id=${encodeURIComponent(chatbotId)}&limit=50&skip=${skip}`;
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      if (data.success && Array.isArray(data.cards)) {
        // Deduplicate cards to prevent duplicate key errors
        setHistoryCards((prev) => {
          const existingIds = new Set(prev.map(card => card._id));
          const newCards = data.cards.filter(card => !existingIds.has(card._id));
          return [...prev, ...newCards];
        });
        setHasMoreHistory(data.has_more || false);
      } else {
        throw new Error(data.error || "Failed to load more history.");
      }
    } catch (e) {
      console.error("Error loading more history:", e);
      // Don't show error for infinite scroll failures
    } finally {
      // Clear both ref and state
      loadingMoreHistoryRef.current = false;
      setLoadingMoreHistory(false);
    }
  }, [chatbotId, hasMoreHistory, historyCards.length]);

  // Infinite scroll for history cards
  useEffect(() => {
    const historyList = historyListRef.current;
    if (!historyList || historyCards.length === 0) {
      return;
    }

    // Wait a tick to ensure DOM has been updated with cards
    const setupTimeoutId = setTimeout(() => {
      let scrollTimeout = null;
      let lastCheckTime = 0;

      const checkAndLoadMore = () => {
        if (!historyList) return;
        
        // Throttle checks to max once per 200ms
        const now = Date.now();
        if (now - lastCheckTime < 200) return;
        lastCheckTime = now;
        
        const { scrollTop, scrollHeight, clientHeight } = historyList;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        // Load more when user scrolls to within 100px of bottom
        // Use ref for immediate check to prevent multiple simultaneous requests
        if (distanceFromBottom < 100 && hasMoreHistory && !loadingMoreHistoryRef.current) {
          loadMoreHistoryCards();
        }
      };

      const handleScroll = () => {
        // Clear any pending timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        
        // Check after scrolling stops (debounced to 200ms)
        scrollTimeout = setTimeout(() => {
          checkAndLoadMore();
        }, 200);
      };

      historyList.addEventListener("scroll", handleScroll, { passive: true });
      
      // Store references for cleanup
      historyList._scrollHandler = handleScroll;
      historyList._scrollTimeout = scrollTimeout;
    }, 100);
    
    return () => {
      clearTimeout(setupTimeoutId);
      if (historyList) {
        if (historyList._scrollHandler) {
          historyList.removeEventListener("scroll", historyList._scrollHandler);
          delete historyList._scrollHandler;
        }
        if (historyList._scrollTimeout) {
          clearTimeout(historyList._scrollTimeout);
          delete historyList._scrollTimeout;
        }
      }
    };
  }, [hasMoreHistory, loadingMoreHistory, loadMoreHistoryCards, historyCards.length]);

  // Auto-open history if historyId query parameter is present.
  //
  // We read `window.location.search` rather than React Router's `location`
  // because the latter is updated through a setState on the Router context;
  // when an event handler does `setCurrentHistoryId(null)` + `navigate(...)`
  // together, the resulting React batching can produce a transient render
  // where currentHistoryId is already null but Router's location string is
  // still the old URL. window.location is the browser's truth and reflects
  // the navigate() synchronously — so the effect always evaluates the same
  // URL the address bar shows.
  //
  // This is what fixes "+ New Chat kicks me back to the previous chat":
  // without the window.location read, the lagging React-Router location made
  // the auto-loader see historyId=A even though we'd just navigated away.
  //
  // We also skip when pendingHistoryLoadRef already points at this id so we
  // never re-arm a load we're already running.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const historyIdParam = params.get("historyId");

    if (!historyIdParam) return;
    if (historyCards.length === 0) return;
    if (!historyCards.some(card => card._id === historyIdParam)) return;
    if (pendingHistoryLoadRef.current === historyIdParam) return;
    // Already showing this history with messages loaded — nothing to do.
    if (currentHistoryId === historyIdParam && messages.length > 0) return;

    handleSelectHistoryCard(historyIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, historyCards, currentHistoryId]);

  const handleToggleHistory = () => setIsHistoryOpen((prev) => !prev);

  // Listen for toggle history event from mobile header icon
  useEffect(() => {
    const handleToggleEvent = () => {
      handleToggleHistory();
    };
    
    window.addEventListener('toggleChatHistory', handleToggleEvent);
    
    return () => {
      window.removeEventListener('toggleChatHistory', handleToggleEvent);
    };
  }, []);

  // Dispatch history state changes to Layout for icon active state
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('chatHistoryStateChange', {
      detail: { isOpen: isHistoryOpen }
    }));
    
    // Close chatbots sidebar when history opens on mobile
    if (isHistoryOpen && window.innerWidth <= 630) {
      window.dispatchEvent(new CustomEvent('closeChatbotsSidebar'));
    }
  }, [isHistoryOpen]);

  // Listen for chatbots sidebar closing history sidebar
  useEffect(() => {
    const handleCloseChatHistory = () => {
      if (isHistoryOpen) {
        setIsHistoryOpen(false);
      }
    };
    
    window.addEventListener('closeChatHistory', handleCloseChatHistory);
    
    return () => {
      window.removeEventListener('closeChatHistory', handleCloseChatHistory);
    };
  }, [isHistoryOpen]);
  // Scroll to target message after loading from search
  useEffect(() => {
    if (!loadingFromSearch && targetMessageTimestamp && messages.length > 0) {
      // Find the message with matching timestamp
      const targetMessage = messages.find(msg => {
        if (!msg.timestamp || !targetMessageTimestamp) return false;
        // Compare ISO timestamps
        const msgTime = new Date(msg.timestamp).toISOString();
        const targetTime = new Date(targetMessageTimestamp).toISOString();
        return msgTime === targetTime;
      });

      if (targetMessage && messageRefs.current[targetMessage.id]) {
        // Scroll to the message with smooth behavior after a longer delay
        // to ensure the page has fully rendered
        setTimeout(() => {
          const element = messageRefs.current[targetMessage.id];
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center'
            });
            
            // Add a highlight effect
            element.classList.add('message-highlight');
            setTimeout(() => {
              element.classList.remove('message-highlight');
            }, 2000);
          }
        }, 500);
      }

      // Clear the target timestamp after scrolling
      setTargetMessageTimestamp(null);
    }
  }, [loadingFromSearch, targetMessageTimestamp, messages]);

  // Close context menu on outside click or escape
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Don't close if clicking inside the share menu
      if (e.target.closest(".user-selector")) {
        return;
      }
      // Don't close if clicking the menu button or context menu itself
      if (e.target.closest(".history-card-menu-btn") || e.target.closest(".history-context-menu")) {
        return;
      }
      setContextMenu((s) => ({ ...s, visible: false }));
      setShareMenu((s) => ({ ...s, visible: false }));
      setSelectedUsers([]);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setContextMenu((s) => ({ ...s, visible: false }));
        setShareMenu((s) => ({ ...s, visible: false }));
        setSelectedUsers([]);
      }
    };
    window.addEventListener("click", handleGlobalClick);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("click", handleGlobalClick);
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleCardContextMenu = (e, cardId) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, cardId });
  };

  const handleMenuButtonClick = (e, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get button position
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    const menuWidth = 180;
    const menuHeight = 200; // Approximate height
    
    let menuX, menuY;
    
    // On desktop (>1150px), use viewport-based positioning
    // On tablet/mobile (≤1150px), use sidebar-relative positioning
    if (window.innerWidth > 1150) {
      // Desktop: position relative to viewport
      menuX = buttonRect.left;
      menuY = buttonRect.bottom + 4;
      
      // Adjust if menu would go off right edge of viewport
      if (menuX + menuWidth > window.innerWidth - 8) {
        menuX = buttonRect.right - menuWidth;
      }
      
      // Adjust if menu would go off bottom edge of viewport
      if (menuY + menuHeight > window.innerHeight - 8) {
        menuY = buttonRect.top - menuHeight - 4;
      }
      
      // Ensure menu doesn't go off left edge
      if (menuX < 8) {
        menuX = 8;
      }
    } else {
      // Tablet/Mobile: position relative to sidebar panel
      const sidebarPanel = document.querySelector('.history-sidebar-panel');
      const sidebarRect = sidebarPanel ? sidebarPanel.getBoundingClientRect() : null;
      
      // Calculate position relative to sidebar panel
      menuX = buttonRect.left - (sidebarRect ? sidebarRect.left : 0);
      menuY = buttonRect.bottom - (sidebarRect ? sidebarRect.top : 0) + 4;
      
      // Get sidebar width for boundary checks
      const sidebarWidth = sidebarRect ? sidebarRect.width : window.innerWidth;
      const sidebarHeight = sidebarRect ? sidebarRect.height : window.innerHeight;
      
      // Adjust if menu would go off right edge of sidebar
      if (menuX + menuWidth > sidebarWidth - 8) {
        menuX = Math.max(8, sidebarWidth - menuWidth - 8);
      }
      
      // Adjust if menu would go off bottom edge of sidebar
      if (menuY + menuHeight > sidebarHeight - 8) {
        menuY = buttonRect.top - (sidebarRect ? sidebarRect.top : 0) - menuHeight - 4;
      }
      
      // Ensure menu doesn't go off left edge
      if (menuX < 8) {
        menuX = 8;
      }
    }
    
    setContextMenu({ 
      visible: true, 
      x: menuX,
      y: menuY, 
      cardId 
    });
  };

  const handleDeleteConversation = async (cardId) => {
    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to delete");
      setHistoryCards((prev) => prev.filter((c) => c._id !== cardId));
      if (currentHistoryIdRef.current === cardId) {
        setCurrentHistoryId(null);
        currentHistoryIdRef.current = null;
        setMessages([]);
        lastSeenSeqRef.current = 0;
        setHasMoreMessages(false);
        setTotalMessageCount(0);
        // Inform server to reset context so next message starts a new conversation
        if (
          wsInstance.current &&
          wsInstance.current.readyState === WebSocket.OPEN
        ) {
          try {
            wsInstance.current.send(
              JSON.stringify({ type: "init", chatbot_id: chatbotId })
            );
          } catch (e) {
            // ignore
          }
        }
      }
    } catch (err) {
      setHistoryError(err.message || "Failed to delete conversation.");
    } finally {
      setContextMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleDuplicateConversation = async (cardId) => {
    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}/duplicate`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to duplicate");
      const card = data.card;
      // Add to top of history list optimistically
      setHistoryCards((prev) => [{ ...card }, ...(prev || [])]);
      setContextMenu((s) => ({ ...s, visible: false }));
    } catch (err) {
      setHistoryError(err.message || "Failed to duplicate conversation.");
      setContextMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleShareClick = (e, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    // Position share menu to touch the right edge of context menu
    // Use setTimeout to ensure the context menu is rendered and we can measure it
    setTimeout(() => {
      const contextMenuElement = document.querySelector(
        ".history-context-menu"
      );
      const contextMenuWidth = contextMenuElement
        ? contextMenuElement.offsetWidth
        : 180;
      setShareMenu({
        visible: true,
        x: contextMenu.x + contextMenuWidth, // Position at the right edge
        y: contextMenu.y,
        cardId: cardId,
      });
    }, 0);
    setSelectedUsers([]);
  };

  const handleShareConversation = async (cardId, users) => {
    if (!users || users.length === 0) {
      setHistoryError("Please select at least one user to share with.");
      return;
    }

    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_emails: users.map((u) => u.email) }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to share");

      // Close menus and reset state
      setShareMenu((s) => ({ ...s, visible: false }));
      setContextMenu((s) => ({ ...s, visible: false }));
      setSelectedUsers([]);

      // Optionally show success message or refresh history
      // The shared conversations will appear in the recipients' history
    } catch (err) {
      setHistoryError(err.message || "Failed to share conversation.");
      setShareMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleEditTitle = (cardId, currentTitle) => {
    setEditingTitleId(cardId);
    setEditingTitleValue(currentTitle || "Untitled");
    setContextMenu((s) => ({ ...s, visible: false }));
  };

  const handleSaveTitle = async (cardId) => {
    const newTitle = editingTitleValue.trim() || "Untitled";

    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ title: newTitle }),
        }
      );
      const data = await res.json();
      if (!data.success)
        throw new Error(data.error || "Failed to update title");

      // Update the card optimistically
      setHistoryCards((prev) =>
        prev.map((c) =>
          c._id === cardId ? { ...c, title: data.title || newTitle } : c
        )
      );

      setEditingTitleId(null);
      setEditingTitleValue("");
    } catch (err) {
      setHistoryError(err.message || "Failed to update title.");
      setEditingTitleId(null);
      setEditingTitleValue("");
    }
  };

  const handleCancelEditTitle = () => {
    setEditingTitleId(null);
    setEditingTitleValue("");
  };

  // Transform raw backend agent_activity into frontend-friendly format.
  // Backend stores: agent_thinking, agent_tool_use, agent_tool_result as separate events.
  // Frontend expects: thinking, tool_use (with result merged in), text blocks.
  const _hydrateAgentActivity = (rawActivity) => {
    if (!rawActivity || rawActivity.length === 0) return [];
    const result = [];
    const toolMap = {}; // tool_id -> index in result array

    for (const evt of rawActivity) {
      if (evt.type === "agent_thinking") {
        result.push({
          type: "thinking",
          thinking: evt.thinking || "",
          streaming: false,
          timestamp: evt.timestamp || Date.now(),
        });
      } else if (evt.type === "agent_tool_use") {
        const idx = result.length;
        toolMap[evt.tool_id] = idx;
        result.push({
          type: "tool_use",
          tool_id: evt.tool_id,
          tool_name: evt.tool_name,
          tool_input: evt.tool_input || {},
          status: "completed", // Stored history is always complete
          timestamp: evt.timestamp || Date.now(),
        });
      } else if (evt.type === "agent_tool_result") {
        const toolIdx = toolMap[evt.tool_use_id];
        if (toolIdx !== undefined && result[toolIdx]) {
          result[toolIdx] = {
            ...result[toolIdx],
            result_content: evt.content,
            status: evt.is_error ? "error" : "completed",
            is_error: evt.is_error || false,
          };
        }
      } else if (evt.type === "text") {
        // Text blocks are already in the right format
        result.push(evt);
      } else if (evt.type === "thinking" || evt.type === "tool_use") {
        // Already in frontend format (shouldn't happen, but handle gracefully)
        result.push(evt);
      }
    }
    return result;
  };

  const handleSelectHistoryCard = async (cardId) => {
    // Claim the slot synchronously before any await — a later call (e.g. user
    // click while the auto-loader's fetch is in flight) becomes the "latest"
    // and our post-await guard will bail.
    pendingHistoryLoadRef.current = cardId;
    // Move the URL into sync with the user's intent BEFORE the fetch. If we
    // wait until after fetch+state, there's a window where state has switched
    // to cardId but the URL still points at the previous history; the
    // auto-loader effect can fire in that window, see the mismatch, and kick
    // off a competing handleSelectHistoryCard for the stale URL — the
    // observed "click B, briefly switches, ends up back on A" bug.
    if (currentHistoryIdRef.current !== cardId) {
      currentHistoryIdRef.current = cardId;
      navigate(`/chat?id=${chatbotId}&historyId=${cardId}`, { replace: true });
    }
    try {
      // Fetch history with only the last 20 messages (pagination)
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}?limit=20&skip=0`,
        { credentials: "include" }
      );
      // Bail if a newer load has been requested while we were fetching.
      if (pendingHistoryLoadRef.current !== cardId) return;
      const data = await res.json();
      if (pendingHistoryLoadRef.current !== cardId) return;
      if (data.success && data.history) {
        const historySelectedModel =
          data.history.selected_model || chatbot?.model || "";
        const historySelectedProvider = String(
          data.history.selected_provider || chatbot?.provider || "openai"
        ).toLowerCase();
        const historyProviderLock = data.history.function_provider_lock
          ? String(data.history.function_provider_lock).toLowerCase()
          : null;
        setSelectedModel(historySelectedModel);
        setSelectedModelProvider(historySelectedProvider);
        setProviderLock(historyProviderLock);

        // Store pagination metadata
        setTotalMessageCount(data.history.total_messages || 0);
        setHasMoreMessages(data.history.has_more || false);

        const msgs = (data.history.messages || [])
          .map((m, idx) => {
            const baseMsg = {
              id: `${cardId}-${idx}`,
              role: m.role,
              content: m.content || "",
              timestamp: m.timestamp || new Date().toISOString(),
              fromHistory: true,
              attachments: m.attachments || undefined, // Include attachments from history
              functionCallStatus:
                m.event === "function_call_complete" ? "completed" : undefined,
              webSearchStatus:
                m.event === "web_search_started"
                  ? "started"
                  : m.event === "web_search_completed"
                  ? "completed"
                  : undefined,
              webSearchAction: m.web_search_action,
              webSearchQuery: m.web_search_query,
              webSearchDomains: m.web_search_domains,
              webSearchSources: m.web_search_sources,
              imageGenerationStatus:
                m.event === "image_generation_completed"
                  ? "completed"
                  : undefined,
              generatedImage: m.image_path
                ? `${config.api.baseUrl}${
                    config.api.endpoints.chatbot.base
                  }/files/${encodeURIComponent(m.image_path)}`
                : null,
              revisedPrompt: m.revised_prompt || null,
              // Agent mode fields
              agentMode: m.agent_mode || false,
              agentActivity: m.agent_mode
                ? _hydrateAgentActivity(m.agent_activity || [])
                : undefined,
            };

            // Don't show function call result messages as separate messages in UI
            // They're stored for chatbot context but shouldn't clutter the UI
            if (m.event === "function_call_result") {
              return null; // Filter these out
            }

            return baseMsg;
          })
          .filter(Boolean); // Remove null entries
        setMessages(msgs);

        setCurrentHistoryId(cardId);
        // currentHistoryIdRef + URL were already updated synchronously at the
        // top of this function (before the await) to avoid a stale-URL window.
        // Reset event seq tracking when switching histories — the new history
        // has its own monotonic seq sequence and we want to receive its full
        // replay (server treats seq > last_seen_seq, so 0 means "send all").
        lastSeenSeqRef.current = 0;

        // if ws is open, re-init with this history id to ensure server writes to same doc
        if (
          wsInstance.current &&
          wsInstance.current.readyState === WebSocket.OPEN
        ) {
          try {
            wsInstance.current.send(
              JSON.stringify({
                type: "init",
                chatbot_id: chatbotId,
                chat_history_id: cardId,
                last_seen_seq: 0,
                selected_model: historySelectedModel || undefined,
                selected_provider: historySelectedProvider || undefined,
              })
            );
          } catch (e) {
            // ignore
          }
        }

        // Close sidebar on mobile (630px and below)
        if (window.innerWidth <= 630) {
          setIsHistoryOpen(false);
        }
        
        // Clear loading state after a delay to ensure messages are fully rendered
        setTimeout(() => {
          setLoadingFromSearch(false);
        }, 500);
      } else {
        throw new Error(data.error || "Failed to load chat history.");
      }
    } catch (e) {
      setChatError(e.message || "Failed to load chat history.");
      setLoadingFromSearch(false);
    }
  };

  // Function to load older messages when scrolling to top
  const loadOlderMessages = useCallback(async () => {
    if (!currentHistoryId || loadingOlderMessagesRef.current || !hasMoreMessages) {
      return;
    }

    loadingOlderMessagesRef.current = true;
    setLoadingOlderMessages(true);
    try {
      const skip = messages.length;
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${currentHistoryId}?limit=20&skip=${skip}`,
        { credentials: "include" }
      );
      const data = await res.json();
      
      if (data.success && data.history) {
        const olderMsgs = (data.history.messages || [])
          .map((m, idx) => {
            const baseMsg = {
              id: `${currentHistoryId}-older-${skip}-${idx}`,
              role: m.role,
              content: m.content || "",
              timestamp: m.timestamp || new Date().toISOString(),
              fromHistory: true,
              attachments: m.attachments || undefined,
              functionCallStatus:
                m.event === "function_call_complete" ? "completed" : undefined,
              webSearchStatus:
                m.event === "web_search_started"
                  ? "started"
                  : m.event === "web_search_completed"
                  ? "completed"
                  : undefined,
              webSearchAction: m.web_search_action,
              webSearchQuery: m.web_search_query,
              webSearchDomains: m.web_search_domains,
              webSearchSources: m.web_search_sources,
              imageGenerationStatus:
                m.event === "image_generation_completed"
                  ? "completed"
                  : undefined,
              generatedImage: m.image_path
                ? `${config.api.baseUrl}${
                    config.api.endpoints.chatbot.base
                  }/files/${encodeURIComponent(m.image_path)}`
                : null,
              revisedPrompt: m.revised_prompt || null,
              // Agent mode fields
              agentMode: m.agent_mode || false,
              agentActivity: m.agent_mode
                ? _hydrateAgentActivity(m.agent_activity || [])
                : undefined,
            };

            if (m.event === "function_call_result") {
              return null;
            }

            return baseMsg;
          })
          .filter(Boolean);

        // Save current scroll position and height before adding messages
        const container = messagesContainerRef.current;
        if (!container) return;
        
        const oldScrollTop = container.scrollTop;
        const oldScrollHeight = container.scrollHeight;

        // Set flag to prevent other scroll logic from interfering
        adjustingScrollRef.current = true;

        // Prepend older messages to the beginning
        setMessages((prev) => [...olderMsgs, ...prev]);
        setHasMoreMessages(data.history.has_more || false);

        // Restore scroll position after new messages are rendered
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              const heightDifference = newScrollHeight - oldScrollHeight;
              // Maintain the user's view by adjusting scroll position by the added height
              const newScrollTop = oldScrollTop + heightDifference;
              container.scrollTop = newScrollTop;
              
            }
            // Clear loading state after scroll position is restored
            setTimeout(() => {
              adjustingScrollRef.current = false;
              loadingOlderMessagesRef.current = false;
              setLoadingOlderMessages(false);
            }, 50);
          });
        });
      }
    } catch (e) {
      console.error("Error loading older messages:", e);
      loadingOlderMessagesRef.current = false;
      setLoadingOlderMessages(false);
    }
  }, [currentHistoryId, hasMoreMessages, messages.length]);

  // Scroll listener for loading older messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop } = container;
      syncAutoScrollState();
      
      // Load more when user scrolls within 100px of the top
      if (scrollTop < 100 && hasMoreMessages && !loadingOlderMessagesRef.current) {
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [hasMoreMessages, loadOlderMessages, messages.length, syncAutoScrollState]);


  // Format a UTC ISO string to local MST time (subtract 7 hours) and show only h:mm AM/PM
  const formatMSTTimeFromUTC = (isoString) => {
    try {
      const parsed = new Date(isoString);
      if (isNaN(parsed.getTime())) return "";
      const adjusted = new Date(parsed.getTime() - 7 * 60 * 60 * 1000);
      return adjusted.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  // Choose correct display format based on message origin
  const formatMessageTime = (message) => {
    const ts = message?.timestamp;
    if (!ts) return "";
    if (message?.fromHistory) {
      return formatMSTTimeFromUTC(ts);
    }
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const submitMessage = async (content, filesToSubmit) => {
    // Allow sending if there's text OR attachments
    if (
      (!content.trim() && filesToSubmit.length === 0) ||
      !wsInstance.current ||
      wsInstance.current.readyState !== WebSocket.OPEN
    ) {
      if (!isConnected) setChatError("Cannot send: Not connected.");
      return;
    }

    const userMessageContent = content.trim();

    const userMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: userMessageContent,
      timestamp: new Date().toISOString(),
      attachments: filesToSubmit, // Store the actual files with the message object for UI rendering
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setChatError(null);

    let attachmentsPayload = [];

    // If there are files, read and encode them
    if (filesToSubmit.length > 0) {
      const readFilesPromises = filesToSubmit.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file); // Reads as Base64 Data URL
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              content: reader.result, // The Base64 string
            });
          };
          reader.onerror = (error) => reject(error);
        });
      });

      try {
        // Wait for all files to be read before continuing
        attachmentsPayload = await Promise.all(readFilesPromises);
      } catch (error) {
        console.error("Error reading attached files:", error);
        setChatError("Failed to read attachments. Message not sent.");
        // Optional: remove the optimistic user message from UI if file reading fails
        setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
        return;
      }
    }

    // Now clear the attachments from the UI since they are processed
    setAttachedFiles([]);
    // Revoke all preview URLs and clear the preview state
    attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    setAttachmentPreviews([]);

    const payload = {
      type: "user_message",
      content: userMessageContent,
      attachments: attachmentsPayload,
      web_search_enabled: webSearchEnabled, // Include web search toggle state
      selected_model: selectedModel || chatbot?.model || "",
      selected_provider:
        selectedModelProvider ||
        String(chatbot?.provider || "openai").toLowerCase(),
    };
    if (currentHistoryIdRef.current) {
      payload.chat_history_id = currentHistoryIdRef.current;
    }
    wsInstance.current.send(JSON.stringify(payload));

    // Optimistically update history card timestamp and move it to top
    if (currentHistoryIdRef.current) {
      // Use current UTC timestamp; formatting logic handles display
      const nowIso = new Date().toISOString();
      setHistoryCards((prev) => {
        const cards = Array.isArray(prev) ? [...prev] : [];
        const idx = cards.findIndex(
          (c) => c._id === currentHistoryIdRef.current
        );
        if (idx === -1) return prev; // Card not found; it will be added by history_opened if needed
        const card = cards[idx];
        const updatedCard = {
          ...card,
          updated_at: nowIso,
          fromLocal: true,
          message_count: (card.message_count || 0) + 1,
        };
        cards.splice(idx, 1);
        cards.unshift(updatedCard);
        return cards;
      });
    }
  };

  const handleSendFromEditor = () => {
    const md = getEditorMarkdown();
    if (!md.trim() && attachedFiles.length === 0) return;
    submitMessage(md, attachedFiles);
    clearEditor();
  };

  const handleSendMessage = () => {
    // Fallback if ever used; route to editor send
    handleSendFromEditor();
  };

  const handleEditMessage = (messageId, currentContent) => {
    setEditingMessageId(messageId);
    setEditedContent(currentContent);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editedContent.trim()) {
      // Don't save empty edits
      setEditingMessageId(null);
      setEditedContent("");
      return;
    }

    // Find the index of the message being edited
    const messageIndex = messages.findIndex((msg) => msg.id === messageId);
    if (messageIndex === -1) {
      setEditingMessageId(null);
      setEditedContent("");
      return;
    }

    const originalMessage = messages[messageIndex];
    const originalContent = originalMessage.content;

    // Remove all messages after the edited message
    const updatedMessages = messages.slice(0, messageIndex + 1);

    // Update the edited message
    updatedMessages[messageIndex] = {
      ...updatedMessages[messageIndex],
      content: editedContent.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(updatedMessages);
    setEditingMessageId(null);
    setEditedContent("");

    // Send the edited message as a new message to continue the conversation
    if (
      wsInstance.current &&
      wsInstance.current.readyState === WebSocket.OPEN
    ) {
      const payload = {
        type: "user_message",
        content: editedContent.trim(),
        attachments: [], // Edit doesn't support attachments for now
        web_search_enabled: webSearchEnabled,
        selected_model: selectedModel || chatbot?.model || "",
        selected_provider:
          selectedModelProvider ||
          String(chatbot?.provider || "openai").toLowerCase(),
        is_edit: true, // Flag to indicate this is an edit
        edit_original_content: originalContent, // Original content to find the message in history
      };
      if (currentHistoryIdRef.current) {
        payload.chat_history_id = currentHistoryIdRef.current;
      }
      wsInstance.current.send(JSON.stringify(payload));

      // Update history card
      if (currentHistoryIdRef.current) {
        const nowIso = new Date().toISOString();
        setHistoryCards((prev) => {
          const cards = Array.isArray(prev) ? [...prev] : [];
          const idx = cards.findIndex(
            (c) => c._id === currentHistoryIdRef.current
          );
          if (idx === -1) return prev;
          const card = cards[idx];
          const updatedCard = {
            ...card,
            updated_at: nowIso,
            fromLocal: true,
            message_count: (card.message_count || 0) + 1,
          };
          cards.splice(idx, 1);
          cards.unshift(updatedCard);
          return cards;
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent("");
  };

  const handleCopyMessage = async (content, messageId) => {
    try {
      // Strip <think>...</think> blocks so only the response is copied
      const { responseContent, thinkingContent } = parseThinkingContent(content);
      const textToCopy = thinkingContent !== null ? responseContent : content;
      await navigator.clipboard.writeText(textToCopy);
      // Show confirmation popup
      setCopiedMessageId(messageId);
      // Auto-hide after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy message:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        // Show confirmation popup even for fallback
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleStopResponse = () => {
    if (
      !wsInstance.current ||
      wsInstance.current.readyState !== WebSocket.OPEN
    ) {
      console.error("*** FRONTEND: Cannot send stop - WebSocket not ready ***");
      return;
    }

    // Send stop message to backend
    try {
      const stopMessage = {
        type: "stop_response",
        response_id: currentBotMessageId.current,
      };
      wsInstance.current.send(JSON.stringify(stopMessage));

      // Immediately update UI state
      setIsResponding(false);

      // Remove loading dots from the current message if it exists
      if (currentBotMessageId.current) {
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            // Check if this message matches the current bot message ID
            if (
              msg.id === currentBotMessageId.current ||
              msg.id === `${currentBotMessageId.current}_response`
            ) {
              // Remove typing placeholder if it exists
              if (msg.isTypingPlaceholder) {
                return {
                  ...msg,
                  isTypingPlaceholder: false,
                };
              }
            }
            return msg;
          });
        });
      }

      currentBotMessageId.current = null;
    } catch (error) {
      console.error("*** FRONTEND: Error sending stop message ***", error);
    }
  };

  const handleStarterQuestionClick = (question) => {
    submitMessage(question, []);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      // Add the raw File objects to the state for submission
      setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);

      // Create new preview objects with URLs for rendering
      const newPreviews = newFiles.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
      }));
      setAttachmentPreviews((prevPreviews) => [
        ...prevPreviews,
        ...newPreviews,
      ]);
    }
    // Reset the file input's value to allow selecting the same file again.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // New handler to remove a specific file
  const handleRemoveFile = (fileIndex) => {
    // Get the preview to revoke its URL before removing from state
    const previewToRemove = attachmentPreviews[fileIndex];
    if (previewToRemove) {
      URL.revokeObjectURL(previewToRemove.url);
    }

    // Remove the file and its preview from their respective states
    setAttachedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
    setAttachmentPreviews((prevPreviews) =>
      prevPreviews.filter((_, index) => index !== fileIndex)
    );
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging files (not text or other elements)
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      // Don't show overlay if hovering over history sidebar or its children
      const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
      if (!historyElements) {
        setIsDraggingFile(true);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're over the history sidebar
    const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
    if (historyElements) {
      // Hide overlay if we move over the history sidebar
      setIsDraggingFile(false);
    } else if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      // Show overlay if we're dragging files over the main chat area
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only hide overlay if leaving the main chat area (not just moving between child elements)
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    // Don't process drops on the history sidebar
    const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
    if (historyElements) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      // Add the raw File objects to the state for submission
      setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);

      // Create new preview objects with URLs for rendering
      const newPreviews = newFiles.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
      }));
      setAttachmentPreviews((prevPreviews) => [
        ...prevPreviews,
        ...newPreviews,
      ]);
    }
  };

  const normalizeWebSearchSources = (sources) => {
    if (!Array.isArray(sources)) return [];
    return sources
      .map((source) => {
        if (!source || typeof source !== "object") return null;
        const url = typeof source.url === "string" ? source.url.trim() : "";
        if (!url) return null;
        return {
          url,
          title:
            typeof source.title === "string" && source.title.trim()
              ? source.title.trim()
              : "",
        };
      })
      .filter(Boolean);
  };

  const linkifyAnnotationTokens = (content, sources) => {
    if (!content || typeof content !== "string") return content || "";
    const normalizedSources = normalizeWebSearchSources(sources);
    if (!normalizedSources.length) return content;

    let fallbackCounter = 0;
    let linked = content.replace(ANNOTATION_TOKEN_REGEX, (match, indexText) => {
      const parsedIndex = Number.parseInt(indexText, 10);
      let source = null;
      let sourceNumber = null;

      if (Number.isInteger(parsedIndex) && parsedIndex > 0) {
        source = normalizedSources[parsedIndex - 1] || null;
        sourceNumber = parsedIndex;
      }
      if (!source) {
        source = normalizedSources[fallbackCounter] || null;
        sourceNumber = fallbackCounter + 1;
        fallbackCounter += 1;
      }
      if (!source?.url) return match;

      return `[${sourceNumber}](<${source.url}>)`;
    });

    let fallbackSquareCounter = 0;
    linked = linked.replace(
      SIMPLE_CITATION_REGEX,
      (match, prefix, indexText) => {
        const parsedIndex = Number.parseInt(indexText, 10);
        let source = null;
        let sourceNumber = null;

        if (Number.isInteger(parsedIndex) && parsedIndex > 0) {
          source = normalizedSources[parsedIndex - 1] || null;
          sourceNumber = parsedIndex;
        }
        if (!source) {
          source = normalizedSources[fallbackSquareCounter] || null;
          sourceNumber = fallbackSquareCounter + 1;
          fallbackSquareCounter += 1;
        }
        if (!source?.url) return match;

        return `${prefix}[${sourceNumber}](<${source.url}>)`;
      }
    );

    return linked;
  };

  const getEffectiveMessageSources = (message, messageIndex) => {
    const ownSources = normalizeWebSearchSources(message?.webSearchSources || []);
    if (ownSources.length) return ownSources;

    const messageId = String(message?.id || "");
    if (messageId.endsWith("_response")) {
      const baseMessageId = messageId.slice(0, -"_response".length);
      const baseMessage = messages.find((m) => m.id === baseMessageId);
      const baseSources = normalizeWebSearchSources(
        baseMessage?.webSearchSources || []
      );
      if (baseSources.length) return baseSources;
    }

    // History entries can store web_search_completed and response text as separate
    // adjacent messages without shared IDs, so look back a few messages.
    for (let i = messageIndex - 1; i >= 0 && i >= messageIndex - 4; i -= 1) {
      const candidate = messages[i];
      if (candidate?.role === "user") break;
      const candidateSources = normalizeWebSearchSources(
        candidate?.webSearchSources || []
      );
      if (candidateSources.length) return candidateSources;
    }

    return [];
  };

  const buildMarkdownComponents = (sources = []) => {
    const normalizedSources = normalizeWebSearchSources(sources);
    const normalizeUrlForLookup = (value) => {
      if (typeof value !== "string") return "";
      const trimmed = value.trim();
      if (!trimmed) return "";
      return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
    };
    const sourceByUrl = new Map(
      normalizedSources.map((source) => [
        normalizeUrlForLookup(source.url),
        source,
      ])
    );

    const getNodeText = (children) => {
      if (typeof children === "string") return children;
      if (!Array.isArray(children)) return "";
      return children
        .map((child) => {
          if (typeof child === "string") return child;
          if (child?.props?.children) return getNodeText(child.props.children);
          return "";
        })
        .join("");
    };

    return {
      a: ({ node, children, ...props }) => {
        const href = props.href;
        // Handle files served by our backend
        if (href && href.startsWith("/chatbot/files")) {
          return (
            <a
              {...props}
              href={`${config.api.baseUrl}${href}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        }
        // Handle OpenAI internal sandbox/mnt paths (waiting for download)
        if (
          href &&
          (href.startsWith("sandbox:") ||
            href.startsWith("/mnt/data/") ||
            href.includes("/mnt/data/"))
        ) {
          return (
            <span className="file-pending-download" title="Preparing download...">
              <span className="file-loading-spinner"></span>
              <span className="file-pending-text">
                {children || "Preparing file..."}
              </span>
            </span>
          );
        }

        const normalizedHref = normalizeUrlForLookup(href);
        const sourceMetaFromHref = normalizedHref
          ? sourceByUrl.get(normalizedHref)
          : null;
        const linkText = getNodeText(children).trim();
        const numericMatch = linkText.match(/\d+/);
        const citationIndex = numericMatch
          ? Number.parseInt(numericMatch[0], 10)
          : null;
        const sourceMetaFromIndex =
          Number.isInteger(citationIndex) && citationIndex > 0
            ? normalizedSources[citationIndex - 1] || null
            : null;
        const sourceMeta = sourceMetaFromHref || sourceMetaFromIndex;
        const fallbackSourceMeta =
          sourceMeta ||
          (normalizedHref
            ? {
                url: normalizedHref,
                title: "Source",
              }
            : null);
        const isAnnotationLink =
          !!fallbackSourceMeta &&
          (ANNOTATION_TEXT_ONLY_REGEX.test(linkText) ||
            NUMERIC_LINK_TEXT_REGEX.test(linkText));

        if (isAnnotationLink) {
          return (
            <span className="annotation-link-wrapper">
              <a
                {...props}
                className="annotation-link"
                target="_blank"
                rel="noopener noreferrer"
                title={fallbackSourceMeta.title || fallbackSourceMeta.url}
              >
                [{Number.isInteger(citationIndex) ? citationIndex : linkText}]
              </a>
              <span className="annotation-hover-card">
                <span className="annotation-hover-title">
                  {fallbackSourceMeta.title || "Source"}
                </span>
                <span className="annotation-hover-url">
                  {fallbackSourceMeta.url}
                </span>
              </span>
            </span>
          );
        }

        // Default external links
        return (
          <a {...props} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
      code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || "");
        return !inline && match ? (
          <StyledCodeBlock language={match[1]}>{children}</StyledCodeBlock>
        ) : (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
    };
  };

  const renderMarkdownContent = (content, effectiveSources) => {
    const annotationLinkedContent = linkifyAnnotationTokens(
      content || "",
      effectiveSources
    );
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={buildMarkdownComponents(effectiveSources)}
      >
        {annotationLinkedContent}
      </ReactMarkdown>
    );
  };

  // Copy-to-clipboard helper for agent tool outputs
  const copyToClipboard = (text, buttonId) => {
    navigator.clipboard.writeText(text).then(() => {
      const btn = document.getElementById(buttonId);
      if (btn) {
        const prev = btn.textContent;
        btn.textContent = "Copied!";
        setTimeout(() => { btn.textContent = prev; }, 1500);
      }
    }).catch(() => {});
  };

  // Tool-specific icon for agent activity
  const toolIcon = (name) => {
    if (name === "Bash") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    );
    if (name === "Read") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    );
    if (name === "Write") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    );
    if (name === "Edit") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    );
    if (name === "Grep" || name === "Glob") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    );
    if (name === "WebSearch" || name === "WebFetch") return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
      </svg>
    );
    return null;
  };

  // Render tool-specific details (input + result) based on tool type
  const renderToolDetails = (event, idx, messageId) => {
    const input = event.tool_input || {};
    const result = event.result_content || "";
    const isError = event.status === "error";
    const isPending = event.status === "pending";
    const name = event.tool_name || "";
    const resultTruncated = result.length > 2000 ? result.slice(0, 2000) + "\n\u2026 (truncated)" : result;
    const copyBtnId = `copy-${messageId}-${idx}`;

    // ── Bash: terminal-style rendering ──
    if (name === "Bash") {
      return (
        <div className="agent-tool-bash">
          <div className="agent-bash-command">
            <span className="agent-bash-prompt">$</span>
            <code>{input.command || ""}</code>
            <button id={`${copyBtnId}-cmd`} className="agent-copy-btn" onClick={() => copyToClipboard(input.command || "", `${copyBtnId}-cmd`)} title="Copy command">Copy</button>
          </div>
          {result && (
            <div className={`agent-bash-output ${isError ? "error" : ""}`}>
              <pre>{resultTruncated}</pre>
              <button id={`${copyBtnId}-out`} className="agent-copy-btn" onClick={() => copyToClipboard(result, `${copyBtnId}-out`)} title="Copy output">Copy</button>
            </div>
          )}
        </div>
      );
    }

    // ── Read: file content display ──
    if (name === "Read") {
      return (
        <div className="agent-tool-file">
          <div className="agent-file-path">{input.file_path || "unknown"}</div>
          {result && (
            <div className="agent-file-content">
              <pre>{resultTruncated}</pre>
              <button id={copyBtnId} className="agent-copy-btn" onClick={() => copyToClipboard(result, copyBtnId)} title="Copy content">Copy</button>
            </div>
          )}
        </div>
      );
    }

    // ── Write: show what was written ──
    if (name === "Write") {
      return (
        <div className="agent-tool-file">
          <div className="agent-file-path">{input.file_path || "unknown"}</div>
          {input.content && (
            <div className="agent-file-content">
              <pre>{input.content.length > 2000 ? input.content.slice(0, 2000) + "\n\u2026 (truncated)" : input.content}</pre>
              <button id={copyBtnId} className="agent-copy-btn" onClick={() => copyToClipboard(input.content, copyBtnId)} title="Copy content">Copy</button>
            </div>
          )}
          {!isPending && !isError && input.file_path && (
            <a className="agent-sandbox-download-link" href={`${config.api.baseUrl}${config.api.endpoints.chatbot.sandboxFiles}/${input.file_path}`} target="_blank" rel="noopener noreferrer">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Download {input.file_path.split("/").pop()}
            </a>
          )}
        </div>
      );
    }

    // ── Edit: show diff-style old → new ──
    if (name === "Edit") {
      return (
        <div className="agent-tool-file">
          <div className="agent-file-path">{input.file_path || "unknown"}</div>
          {input.old_string && (
            <div className="agent-edit-diff">
              <div className="agent-diff-removed">
                <span className="agent-diff-label">-</span>
                <pre>{input.old_string}</pre>
              </div>
              <div className="agent-diff-added">
                <span className="agent-diff-label">+</span>
                <pre>{input.new_string || ""}</pre>
              </div>
            </div>
          )}
          {!isPending && !isError && input.file_path && (
            <a className="agent-sandbox-download-link" href={`${config.api.baseUrl}${config.api.endpoints.chatbot.sandboxFiles}/${input.file_path}`} target="_blank" rel="noopener noreferrer">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Download {input.file_path.split("/").pop()}
            </a>
          )}
        </div>
      );
    }

    // ── Grep/Glob: search results ──
    if (name === "Grep" || name === "Glob") {
      return (
        <div className="agent-tool-file">
          <div className="agent-file-path">{name === "Grep" ? `Pattern: ${input.pattern || ""}` : `Pattern: ${input.pattern || ""}`}</div>
          {result && (
            <div className="agent-file-content">
              <pre>{resultTruncated}</pre>
              <button id={copyBtnId} className="agent-copy-btn" onClick={() => copyToClipboard(result, copyBtnId)} title="Copy results">Copy</button>
            </div>
          )}
        </div>
      );
    }

    // ── WebSearch/WebFetch ──
    if (name === "WebSearch" || name === "WebFetch") {
      return (
        <div className="agent-tool-file">
          <div className="agent-file-path">{name === "WebSearch" ? `Query: ${input.query || ""}` : input.url || ""}</div>
          {result && (
            <div className="agent-file-content">
              <pre>{resultTruncated}</pre>
              <button id={copyBtnId} className="agent-copy-btn" onClick={() => copyToClipboard(result, copyBtnId)} title="Copy">Copy</button>
            </div>
          )}
        </div>
      );
    }

    // ── Fallback: generic JSON input/output ──
    return (
      <>
        {input && Object.keys(input).length > 0 && (
          <pre className="agent-inline-tool-input">{JSON.stringify(input, null, 2)}</pre>
        )}
        {result && (
          <div className={`agent-inline-tool-result ${isError ? "error" : ""}`}>
            <pre className="agent-inline-tool-result-content">{resultTruncated}</pre>
            <button id={copyBtnId} className="agent-copy-btn" onClick={() => copyToClipboard(result, copyBtnId)} title="Copy">Copy</button>
          </div>
        )}
      </>
    );
  };

  const renderAgentActivity = (agentActivity, messageId, effectiveSources) => {
    if (!agentActivity || agentActivity.length === 0) return null;

    return (
      <div className="agent-inline-timeline">
        {agentActivity.map((event, idx) => {
          if (event.type === "thinking") {
            const isThinkingExpanded = expandedAgentActivity.has(`${messageId}_thinking_${idx}`);
            const toggleThinking = () => {
              setExpandedAgentActivity((prev) => {
                const newSet = new Set(prev);
                const key = `${messageId}_thinking_${idx}`;
                if (newSet.has(key)) newSet.delete(key);
                else newSet.add(key);
                return newSet;
              });
            };
            return (
              <div key={idx} className={`agent-inline-thinking ${event.streaming ? "streaming" : ""}`}>
                <button className="agent-inline-thinking-header" onClick={toggleThinking}>
                  {event.streaming ? (
                    <span className="agent-tool-spinner" />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  )}
                  <span>{event.streaming ? "Thinking..." : "Thinking"}</span>
                  <svg
                    className={`agent-inline-chevron ${isThinkingExpanded ? "expanded" : ""}`}
                    width="12" height="12" viewBox="0 0 16 16"
                  >
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </button>
                {isThinkingExpanded && (
                  <div className="agent-inline-thinking-content">
                    {event.thinking || ""}
                  </div>
                )}
              </div>
            );
          }

          if (event.type === "tool_use") {
            const isPending = event.status === "pending";
            const isError = event.status === "error";
            const isToolExpanded = expandedAgentActivity.has(`${messageId}_tool_${idx}`);
            const toggleTool = () => {
              setExpandedAgentActivity((prev) => {
                const newSet = new Set(prev);
                const key = `${messageId}_tool_${idx}`;
                if (newSet.has(key)) newSet.delete(key);
                else newSet.add(key);
                return newSet;
              });
            };

            const name = event.tool_name || "Tool";
            const input = event.tool_input || {};

            // Format tool description for the header
            const toolDesc = (() => {
              if (name === "Read" && input.file_path) return input.file_path;
              if (name === "Write" && input.file_path) return input.file_path;
              if (name === "Edit" && input.file_path) return input.file_path;
              if (name === "Bash" && input.command) return input.command.length > 80 ? input.command.slice(0, 80) + "..." : input.command;
              if (name === "Glob" && input.pattern) return input.pattern;
              if (name === "Grep" && input.pattern) return `"${input.pattern}"`;
              if (name === "WebSearch" && input.query) return `"${input.query}"`;
              if (name === "WebFetch" && input.url) return input.url;
              return "";
            })();

            return (
              <div key={idx} className={`agent-inline-tool ${isError ? "error" : ""} ${isPending ? "pending" : ""}`}>
                <button className="agent-inline-tool-header" onClick={toggleTool}>
                  <span className="agent-inline-tool-status">
                    {isPending ? (
                      <span className="agent-tool-spinner" />
                    ) : isError ? (
                      <span className="agent-inline-status-icon error">✕</span>
                    ) : (
                      <span className="agent-inline-status-icon success">✓</span>
                    )}
                  </span>
                  <span className="agent-tool-icon">{toolIcon(name)}</span>
                  <span className="agent-tool-label">{name}</span>
                  {toolDesc && <code className="agent-inline-tool-desc">{toolDesc}</code>}
                  <svg
                    className={`agent-inline-chevron ${isToolExpanded ? "expanded" : ""}`}
                    width="12" height="12" viewBox="0 0 16 16"
                  >
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </button>
                {isToolExpanded && (
                  <div className="agent-inline-tool-details">
                    {renderToolDetails(event, idx, messageId)}
                  </div>
                )}
              </div>
            );
          }

          if (event.type === "text") {
            return (
              <div key={idx} className="agent-inline-text">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={buildMarkdownComponents(effectiveSources)}
                >
                  {event.content || ""}
                </ReactMarkdown>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  const renderThinkingBlock = (thinkingContent, isThinking, messageId) => {
    const isExpanded = expandedThinking.has(messageId);
    const toggleExpanded = () => {
      setExpandedThinking((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(messageId)) {
          newSet.delete(messageId);
        } else {
          newSet.add(messageId);
        }
        return newSet;
      });
    };

    return (
      <div className={`thinking-block ${isThinking ? "thinking-active" : "thinking-complete"}`}>
        <button
          className="thinking-block-header"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
        >
          <span className="thinking-block-label">
            {isThinking && <span className="thinking-spinner" />}
            {isThinking ? "Thinking..." : "Thinking Complete"}
          </span>
          <svg
            className={`thinking-block-chevron ${isExpanded ? "expanded" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={`thinking-block-collapse ${isExpanded ? "expanded" : ""}`}>
          <div className="thinking-block-overflow">
            <div className="thinking-block-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {thinkingContent}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMessageMarkdown = (message, messageIndex) => {
    const effectiveSources = getEffectiveMessageSources(message, messageIndex);
    const rawContent = message?.content || "";

    // Check for <think> tags in the content
    const { thinkingContent, responseContent, isThinking } = parseThinkingContent(rawContent);

    // Agent mode: render everything inline in chronological order
    if (message.agentMode) {
      const activity = message.agentActivity || [];
      // If there's activity, render the inline timeline (text is included in agentActivity)
      if (activity.length > 0) {
        return renderAgentActivity(activity, message.id, effectiveSources);
      }
      // Fallback: if no activity yet but there's content, render normally
      if (rawContent) {
        return renderMarkdownContent(rawContent, effectiveSources);
      }
      return null;
    }

    if (thinkingContent !== null) {
      return (
        <>
          {renderThinkingBlock(thinkingContent, isThinking, message.id)}
          {responseContent && renderMarkdownContent(responseContent, effectiveSources)}
        </>
      );
    }

    return renderMarkdownContent(rawContent, effectiveSources);
  };

  if (loadingChatbot) {
    return (
      <Layout>
        <div className="chat-page">
          {loadingFromSearch && (
            <div className="chat-loading-overlay">
              <div className="chat-loading-content">
                <div className="chat-loading-spinner"></div>
                <p className="chat-loading-text">Loading conversation...</p>
              </div>
            </div>
          )}
          <div className="chat-container" style={{ display: loadingFromSearch ? 'none' : 'block' }}>
            <div className="loading">Loading chatbot...</div>
          </div>
        </div>
      </Layout>
    );
  }
  if (fetchError) {
    return (
      <Layout>
        <div className="chat-container">
          <div className="error-message">{fetchError}</div>
        </div>
      </Layout>
    );
  }
  if (!chatbot) {
    return (
      <Layout>
        <div className="chat-container">
          <div className="error-message">Chatbot data not available.</div>
        </div>
      </Layout>
    );
  }

  const hasMessages = messages.length > 0;
  const chatbotVisual = getChatbotVisual();
  const userInitials = getUserInitials();
  const hasStarterQuestions =
    chatbot &&
    Array.isArray(chatbot.starter_questions) &&
    chatbot.starter_questions.length > 0;

  return (
    <Layout>
      <div 
        className="chat-page"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loadingFromSearch && (
          <div className="chat-loading-overlay">
            <div className="chat-loading-content">
              <div className="chat-loading-spinner"></div>
              <p className="chat-loading-text">Loading conversation...</p>
            </div>
          </div>
        )}
        <div className="chat-container" style={{ display: loadingFromSearch ? 'none' : 'block' }}>
          <div
            className={`chat-history-page-container ${
              isHistoryOpen ? "history-open" : "history-closed"
            }`}
            onClick={(e) => {
              // Close history sidebar when clicking outside of it (on backdrop/chat panel)
              // Only at 1150px and below when the sidebar overlays content
              if (
                isHistoryOpen &&
                window.innerWidth <= 1150 &&
                !e.target.closest(".history-sidebar-wrapper")
              ) {
                setIsHistoryOpen(false);
              }
            }}
          >
            <div 
              className="history-sidebar-wrapper"
              onClick={(e) => {
                // Open sidebar when clicking anywhere on it while closed
                if (!isHistoryOpen && !e.target.closest('button')) {
                  handleToggleHistory();
                }
              }}
              style={{ cursor: !isHistoryOpen ? 'pointer' : 'default' }}
            >
              <div className="history-sidebar-panel">
                <div className="history-header">
                  <button
                    className="new-chat-btn"
                    title="Start new conversation"
                    onClick={() => {
                      // Start a fresh conversation without refreshing the page.
                      // Claim the pending-load slot so any in-flight
                      // handleSelectHistoryCard (e.g. one the auto-loader
                      // kicked off on mount) sees its cardId is stale and
                      // bails out instead of overwriting this new-chat state.
                      pendingHistoryLoadRef.current = null;
                      setMessages([]);
                      lastSeenSeqRef.current = 0;
                      setHasMoreMessages(false);
                      setTotalMessageCount(0);
                      setCurrentHistoryId(null);
                      currentHistoryIdRef.current = null;
                      setChatError(null);
                      setInputText("");
                      setSelectedModel(chatbot?.model || "");
                      setSelectedModelProvider(
                        String(chatbot?.provider || "openai").toLowerCase()
                      );
                      setProviderLock(null);
                      // Clear attachments and previews
                      setAttachedFiles([]);
                      attachmentPreviews.forEach((p) =>
                        URL.revokeObjectURL(p.url)
                      );
                      setAttachmentPreviews([]);
                      // Clear URL parameters to prevent auto-loading from search results
                      navigate(`/chat?id=${chatbotId}`, { replace: true });
                      // Inform server to reset conversation context for this chatbot
                      if (
                        wsInstance.current &&
                        wsInstance.current.readyState === WebSocket.OPEN
                      ) {
                        try {
                          wsInstance.current.send(
                            JSON.stringify({
                              type: "init",
                              chatbot_id: chatbotId,
                            })
                          );
                        } catch (e) {
                          // ignore
                        }
                      }
                      // Close sidebar on mobile (630px and below)
                      if (window.innerWidth <= 630) {
                        setIsHistoryOpen(false);
                      }
                    }}
                  >
                    <img src={plusIcon} alt="New" className="new-chat-icon" />
                  </button>
                  <button
                    onClick={handleToggleHistory}
                    className="history-sidebar-toggle-btn"
                    title={isHistoryOpen ? "Close History" : "Open History"}
                  >
                    <img
                      src={historyIconDark}
                      alt="History"
                      className="history-toggle-icon"
                    />
                  </button>
                </div>
                {loadingHistoryCards && (
                  <div className="history-loading">Loading…</div>
                )}
                {historyError && (
                  <div className="history-error">{historyError}</div>
                )}
                {!loadingHistoryCards &&
                  !historyError &&
                  historyCards.length === 0 && (
                    <div className="history-empty">
                      Conversations will be saved here
                    </div>
                  )}
                <div className="history-list" ref={historyListRef}>
                  {historyCards.map((card) => {
                    const isWorking = workingHistoryIds.has(String(card._id));
                    return (
                    <div
                      key={card._id}
                      className={`history-card ${
                        currentHistoryId === card._id ? "active" : ""
                      } ${card.is_shared ? "shared" : ""} ${isWorking ? "working" : ""}`}
                      onContextMenu={(e) => handleCardContextMenu(e, card._id)}
                      onClick={(e) => {
                        // Don't select card if clicking the menu button
                        if (e.target.closest('.history-card-menu-btn')) {
                          return;
                        }
                        if (editingTitleId !== card._id) {
                          handleSelectHistoryCard(card._id);
                          // Close sidebar on mobile (630px and below)
                          if (window.innerWidth <= 630) {
                            setIsHistoryOpen(false);
                          }
                        }
                      }}
                    >
                      <div className="history-card-content">
                        {editingTitleId === card._id ? (
                          <input
                            type="text"
                            className="history-card-title-input"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveTitle(card._id);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelEditTitle();
                              }
                            }}
                            onBlur={() => handleSaveTitle(card._id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="history-card-title">
                            {card.is_imported && (
                              <img
                                src={openaiLogo}
                                alt="Imported from ChatGPT"
                                className="history-card-import-icon"
                                title="Imported from ChatGPT"
                              />
                            )}
                            {card.title || "Untitled"}
                          </div>
                        )}
                        {card.is_shared && card.shared_by_email && (
                          <div className="history-card-shared-by">
                            Shared by: {card.shared_by_email}
                          </div>
                        )}
                        <div className="history-card-sub">
                          {card.message_count || 0} messages ·{" "}
                          {formatHistoryCardTime(card)}
                        </div>
                      </div>
                      <button
                        className="history-card-menu-btn"
                        onClick={(e) => handleMenuButtonClick(e, card._id)}
                        aria-label="Options"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="8" cy="3" r="1.5" />
                          <circle cx="8" cy="8" r="1.5" />
                          <circle cx="8" cy="13" r="1.5" />
                        </svg>
                      </button>
                      {isWorking && (
                        <span className="history-card-working-dot" aria-hidden="true" />
                      )}
                    </div>
                    );
                  })}
                  {loadingMoreHistory && (
                    <div className="history-loading-more">
                      <div className="spinner"></div>
                      <span>Loading more...</span>
                    </div>
                  )}
                  {!loadingMoreHistory && historyCards.length > 0 && !hasMoreHistory && (
                    <div className="history-end-message">No more conversations</div>
                  )}
                </div>
                {contextMenu.visible && (
                  <div
                    className={`history-context-menu ${
                      shareMenu.visible ? "share-menu-open" : ""
                    }`}
                    style={{
                      position: "fixed",
                      left: contextMenu.x,
                      top: contextMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="menu-item duplicate"
                      onClick={() =>
                        handleDuplicateConversation(contextMenu.cardId)
                      }
                    >
                      Duplicate Conversation
                    </div>
                    <div
                      className="menu-item share"
                      onClick={(e) => handleShareClick(e, contextMenu.cardId)}
                    >
                      Share
                    </div>
                    <div
                      className="menu-item"
                      onClick={() => {
                        const card = historyCards.find(
                          (c) => c._id === contextMenu.cardId
                        );
                        if (card) {
                          handleEditTitle(contextMenu.cardId, card.title);
                        }
                      }}
                    >
                      Edit Title
                    </div>
                    <div
                      className="menu-item danger"
                      onClick={() =>
                        handleDeleteConversation(contextMenu.cardId)
                      }
                    >
                      Delete Conversation
                    </div>
                  </div>
                )}
                {shareMenu.visible && (
                  <div
                    className="share-menu-container"
                    style={{
                      position: "fixed",
                      left: shareMenu.x,
                      top: shareMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UserSelector
                      selectedUsers={selectedUsers}
                      onChange={setSelectedUsers}
                      onShare={() =>
                        handleShareConversation(shareMenu.cardId, selectedUsers)
                      }
                      onClose={() => {
                        setShareMenu((s) => ({ ...s, visible: false }));
                        setSelectedUsers([]);
                      }}
                      shareDisabled={selectedUsers.length === 0}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="chat-panel">
              {isDraggingFile && (
                <div className="file-drop-overlay">
                  <div className="file-drop-content">
                    <svg 
                      width="56" 
                      height="56" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p className="file-drop-text">Drop file here to attach</p>
                  </div>
                </div>
              )}
              <div className="chat-div">
                {isConnecting && (
                  <div className="info-message connecting">
                    Connecting to chat...
                  </div>
                )}
                {!isConnected && !isConnecting && !chatError && chatbotId && (
                  <div className="info-message disconnected">
                    Not connected. Attempting to reconnect...
                  </div>
                )}

                <div className="chat-messages" ref={messagesContainerRef}>
                  {!hasMessages ? (
                    <div className="empty-chat-fullheight">
                      {chatbotVisual ? (
                        <div className="welcome-container">
                          <div
                            className="chatbot-icon large-icon"
                            dangerouslySetInnerHTML={{
                              __html:
                                getChatbotVisual("logo") ||
                                getChatbotVisual("icon"),
                            }}
                          />
                          {/* <p className="empty-chat-description">
                        {chatbot.description}
                      </p> */}
                          {hasStarterQuestions && (
                            <div className="starter-questions-container">
                              {chatbot.starter_questions.map(
                                (question, index) => (
                                  <div
                                    key={index}
                                    className="starter-question"
                                    onClick={() =>
                                      handleStarterQuestionClick(question)
                                    }
                                  >
                                    {question}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="welcome-container">
                          <p className="empty-chat-description">
                            {chatbot.description}
                          </p>
                          {hasStarterQuestions && (
                            <div className="starter-questions-container">
                              {chatbot.starter_questions.map(
                                (question, index) => (
                                  <div
                                    key={index}
                                    className="starter-question"
                                    onClick={() =>
                                      handleStarterQuestionClick(question)
                                    }
                                  >
                                    {question}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {loadingOlderMessages && (
                        <div className="loading-older-messages">
                          <div className="spinner"></div>
                          <span>Loading older messages...</span>
                        </div>
                      )}
                      {messages.map((message, messageIndex) => (
                        <div
                          key={message.id}
                          ref={(el) => {
                            if (el) {
                              messageRefs.current[message.id] = el;
                            }
                          }}
                          className={`chat-message ${
                            message.role === "user" ? "user" : "bot"
                          }`}
                        >
                          {message.role === "user" ? (
                            <div className="message-avatar user-avatar">
                              <span>{userInitials}</span>
                            </div>
                          ) : (
                            <div className="message-avatar bot-avatar">
                              {chatbotVisual ? (
                                <div
                                  className="bot-icon"
                                  dangerouslySetInnerHTML={{
                                    __html: chatbotVisual,
                                  }}
                                />
                              ) : (
                                <div className="bot-icon default-icon">
                                  <img
                                    src={ltLogo}
                                    alt="AI Avatar"
                                    className="bot-avatar-svg"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          <div className="message-wrapper">
                            {/* Render attachments OUTSIDE/ABOVE the message content bubble */}
                            {message.role === "user" &&
                              message.attachments &&
                              message.attachments.length > 0 && (
                                <div className="message-attachments-preview">
                                  {message.attachments.map((file, index) => {
                                    // Handle both File objects (live messages) and attachment objects (from history)
                                    const isFileObject = file instanceof File;
                                    // For File objects, create object URL; for history attachments, use content directly
                                    const fileSrc = isFileObject
                                      ? URL.createObjectURL(file)
                                      : file.content; // Backend sends content as data URL string

                                    const isImage =
                                      file.type?.startsWith("image/");
                                    const isPDF =
                                      file.type === "application/pdf" ||
                                      file.name?.toLowerCase().endsWith(".pdf");
                                    const isWordDoc =
                                      file.type === "application/msword" ||
                                      file.type ===
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".doc") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".docx");
                                    const isCSV =
                                      file.type === "text/csv" ||
                                      file.name?.toLowerCase().endsWith(".csv");
                                    const isExcel =
                                      file.type ===
                                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                                      file.type ===
                                        "application/vnd.ms-excel" ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".xlsx") ||
                                      file.name?.toLowerCase().endsWith(".xls");
                                    const isTextFile =
                                      file.type?.startsWith("text/") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".txt") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".md") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".markdown") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".json") ||
                                      file.name?.toLowerCase().endsWith(".js");
                                    const hasPreview =
                                      isPDF ||
                                      isWordDoc ||
                                      isCSV ||
                                      isExcel ||
                                      isTextFile;

                                    return (
                                      <div
                                        key={index}
                                        className="message-attachment-item"
                                      >
                                        {isImage && fileSrc ? (
                                          <img src={fileSrc} alt={file.name} />
                                        ) : file.preview_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/files/${encodeURIComponent(
                                              file.preview_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-pdf-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : isPDF && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/pdf/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-pdf-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : isWordDoc && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/word/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-word-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : hasPreview && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/file/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-word-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : null}
                                        <span
                                          className="message-attachment-name"
                                          title={file.name}
                                        >
                                          {file.name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            <div
                              className={`message-content ${
                                message.role === "assistant" &&
                                (message.functionCallStatus === "started" ||
                                  message.webSearchStatus === "started" ||
                                  (message.functionCallStatus === "completed" &&
                                    !message.content) ||
                                  (message.webSearchStatus === "completed" &&
                                    !message.content))
                                  ? "function-call-padding"
                                  : ""
                              }`}
                            >
                              {/* Text content of the message */}
                              {message.role === "assistant" ? (
                                <>
                                  {/* Prioritize web search status over typing indicator */}
                                  {/* Web Search Status Indicator */}
                                  {message.webSearchStatus === "started" && (
                                    <div className="web-search-status">
                                      <div className="web-search-spinner"></div>
                                      <span>
                                        {message.webSearchAction === "search"
                                          ? "Searching the web..."
                                          : "Browsing..."}
                                      </span>
                                    </div>
                                  )}

                                  {/* Code Interpreter Status Indicator */}
                                  {message.codeInterpreterStatus ===
                                    "started" &&
                                    !message.content && (
                                      <div className="web-search-status">
                                        <div className="web-search-spinner"></div>
                                        <span>Running code...</span>
                                      </div>
                                    )}

                                  {/* Function Call Status Indicator */}
                                  {message.functionCallStatus === "started" && (
                                    <div className="function-call-status">
                                      <div className="function-call-spinner"></div>
                                      <span>
                                        Calling function:{" "}
                                        {message.functionName ||
                                          "External Tool"}
                                        ...
                                      </span>
                                    </div>
                                  )}
                                  {message.webSearchStatus === "completed" &&
                                  !message.content ? (
                                    // Show "Web search complete" briefly, then loading dots will appear
                                    <div className="function-call-status">
                                      Web search complete
                                    </div>
                                  ) : message.codeInterpreterStatus ===
                                      "completed" && !message.content ? (
                                    <div className="function-call-status">
                                      Code interpreter complete
                                    </div>
                                  ) : message.imageGenerationStatus ===
                                    "started" ? (
                                    <div className="function-call-status">
                                      Generating image...
                                    </div>
                                  ) : message.functionCallStatus ===
                                      "completed" && !message.content ? (
                                    <div className="function-call-status">
                                      Function call complete
                                    </div>
                                  ) : message.isTypingPlaceholder &&
                                    !message.content &&
                                    !(message.agentActivity && message.agentActivity.length > 0) &&
                                    message.agentMode ? (
                                    <div className="agent-working-status">
                                      <span className="agent-working-spinner" />
                                      <span>Agent working...</span>
                                    </div>
                                  ) : message.isTypingPlaceholder &&
                                    !message.content &&
                                    !message.webSearchStatus &&
                                    !message.imageGenerationStatus ? (
                                    <div className="typing-indicator">
                                      {message.preparingMessage && (
                                        <span className="preparing-label">
                                          {message.preparingMessage}
                                        </span>
                                      )}
                                      <span className="typing-dot"></span>
                                      <span className="typing-dot"></span>
                                      <span className="typing-dot"></span>
                                    </div>
                                  ) : null}
                                  {/* Show image if completed - display BEFORE text content */}
                                  {message.imageGenerationStatus ===
                                    "completed" &&
                                    message.generatedImage && (
                                      <div className="generated-image-container">
                                        {message.revisedPrompt && (
                                          <div className="image-prompt">
                                            <div className="image-prompt-header">
                                              <span className="image-prompt-label">
                                                Revised Prompt:
                                              </span>
                                              <button
                                                className="image-prompt-toggle"
                                                onClick={() => {
                                                  setExpandedPrompts((prev) => {
                                                    const newSet = new Set(
                                                      prev
                                                    );
                                                    if (
                                                      newSet.has(message.id)
                                                    ) {
                                                      newSet.delete(message.id);
                                                    } else {
                                                      newSet.add(message.id);
                                                    }
                                                    return newSet;
                                                  });
                                                }}
                                                aria-label={
                                                  expandedPrompts.has(
                                                    message.id
                                                  )
                                                    ? "Collapse prompt"
                                                    : "Expand prompt"
                                                }
                                              >
                                                {expandedPrompts.has(
                                                  message.id
                                                ) ? (
                                                  <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  >
                                                    <polyline points="18 15 12 9 6 15"></polyline>
                                                  </svg>
                                                ) : (
                                                  <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  >
                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                  </svg>
                                                )}
                                              </button>
                                            </div>
                                            <div
                                              className={`image-prompt-content ${
                                                expandedPrompts.has(message.id)
                                                  ? "expanded"
                                                  : "collapsed"
                                              }`}
                                            >
                                              {expandedPrompts.has(
                                                message.id
                                              ) ? (
                                                message.revisedPrompt
                                              ) : (
                                                <>
                                                  {message.revisedPrompt
                                                    .length > 150
                                                    ? `${message.revisedPrompt.substring(
                                                        0,
                                                        150
                                                      )}...`
                                                    : message.revisedPrompt}
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        <div className="image-wrapper">
                                          <img
                                            src={message.generatedImage}
                                            alt="AI generated content"
                                            className="generated-image"
                                            onError={(e) => {
                                              console.error(
                                                "*** FRONTEND: Image failed to load ***",
                                                {
                                                  src: message.generatedImage?.substring(
                                                    0,
                                                    100
                                                  ),
                                                  hasImage:
                                                    !!message.generatedImage,
                                                  imageLength:
                                                    message.generatedImage
                                                      ?.length,
                                                }
                                              );
                                              e.target.style.display = "none";
                                            }}
                                            onLoad={() => {}}
                                          />
                                          <button
                                            className="download-image-button"
                                            onClick={async () => {
                                              try {
                                                // Fetch the image as a blob
                                                const response = await fetch(
                                                  message.generatedImage
                                                );
                                                const blob =
                                                  await response.blob();

                                                // Create a blob URL
                                                const blobUrl =
                                                  window.URL.createObjectURL(
                                                    blob
                                                  );

                                                // Create a temporary link and trigger download
                                                const link =
                                                  document.createElement("a");
                                                link.href = blobUrl;
                                                link.download = `generated-image-${Date.now()}.png`;
                                                link.style.display = "none";
                                                document.body.appendChild(link);
                                                link.click();

                                                // Clean up
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(
                                                  blobUrl
                                                );
                                              } catch (error) {
                                                console.error(
                                                  "Error downloading image:",
                                                  error
                                                );
                                                // Fallback: open in new tab if download fails
                                                window.open(
                                                  message.generatedImage,
                                                  "_blank"
                                                );
                                              }
                                            }}
                                            title="Download image"
                                          >
                                            <svg
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="download-icon"
                                            >
                                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                              <polyline points="7 10 12 15 17 10"></polyline>
                                              <line
                                                x1="12"
                                                y1="15"
                                                x2="12"
                                                y2="3"
                                              ></line>
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  {/* Show text content if present (can be shown alongside image) */}
                                  {message.content && (
                                    renderMessageMarkdown(
                                      message,
                                      messageIndex
                                    )
                                  )}
                                </>
                              ) : editingMessageId === message.id ? (
                                <textarea
                                  className="edit-message-textarea"
                                  value={editedContent}
                                  onChange={(e) =>
                                    setEditedContent(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveEdit(message.id);
                                    } else if (e.key === "Escape") {
                                      handleCancelEdit();
                                    }
                                  }}
                                  autoFocus
                                  rows={Math.min(
                                    Math.max(
                                      editedContent.split("\n").length,
                                      1
                                    ),
                                    10
                                  )}
                                />
                              ) : (
                                renderMessageMarkdown(message, messageIndex)
                              )}
                            </div>
                            <div className="message-footer">
                              <div className="message-timestamp">
                                {formatMessageTime(message)}
                              </div>
                              <div className="message-actions">
                                {message.role === "user" &&
                                  message.content &&
                                  !editingMessageId && (
                                    <button
                                      className="edit-message-button"
                                      onClick={() =>
                                        handleEditMessage(
                                          message.id,
                                          message.content
                                        )
                                      }
                                      title="Edit message"
                                    >
                                      <svg
                                        className="edit-icon"
                                        viewBox="0 0 512 512"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                      >
                                        <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152L0 424c0 48.6 39.4 88 88 88l272 0c48.6 0 88-39.4 88-88l0-112c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 112c0 22.1-17.9 40-40 40L88 464c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l112 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 64z" />
                                      </svg>
                                    </button>
                                  )}
                                {message.content && (
                                  <div className="copy-button-wrapper">
                                    <button
                                      className="copy-message-button"
                                      onClick={() =>
                                        handleCopyMessage(
                                          message.content,
                                          message.id
                                        )
                                      }
                                      title="Copy message"
                                    >
                                      <svg
                                        className="copy-icon"
                                        viewBox="0 0 448 512"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                      >
                                        <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                                      </svg>
                                    </button>
                                    {copiedMessageId === message.id && (
                                      <div className="copy-confirmation-popup">
                                        Copied!
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Attached Files Preview */}
                {attachmentPreviews.length > 0 && (
                  <div className="attached-files-container">
                    {attachmentPreviews.map((preview, index) => (
                      <div key={index} className="file-card">
                        {preview.file.type.startsWith("image/") ? (
                          <img
                            src={preview.url}
                            alt={preview.file.name}
                            className="file-preview-image"
                          />
                        ) : null}
                        <div className="file-details">
                          <span className="file-name" title={preview.file.name}>
                            {preview.file.name}
                          </span>
                          <span className="file-size">
                            {formatFileSize(preview.file.size)}
                          </span>
                        </div>
                        <button
                          className="remove-file-button"
                          onClick={() => handleRemoveFile(index)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="chat-input-wrapper">
                  <div
                    className="chat-model-dropdown chat-model-dropdown-floating"
                    ref={modelMenuRef}
                  >
                    <button
                      type="button"
                      className="chat-model-dropdown-trigger"
                      onClick={() =>
                        modelOptions.length && setIsModelMenuOpen((prev) => !prev)
                      }
                      title={
                        chatbot?.vector_store_id
                          ? "This chatbot has a vector store. Only OpenAI models are available."
                          : "Select model for this conversation"
                      }
                      disabled={!modelOptions.length}
                    >
                      <span className="chat-model-selected-text">
                        {selectedModelMeta?.name ||
                          selectedModel ||
                          "No models available"}
                      </span>
                      <img
                        src={downCaretIcon}
                        alt="Open model options"
                        className={`chat-model-caret ${
                          isModelMenuOpen ? "open" : ""
                        }`}
                      />
                    </button>

                    {isModelMenuOpen && (
                      <div className="chat-model-dropdown-menu">
                        {modelOptions.length > 0 && groupedModelOptions.map((group) => (
                          <div
                            key={group.provider}
                            className="chat-model-dropdown-group"
                          >
                            <div className="chat-model-dropdown-group-label">
                              {group.label}
                            </div>
                            {group.options.map((model) => (
                              <button
                                key={model.id}
                                type="button"
                                className={`chat-model-dropdown-option ${
                                  model.id === selectedModel ? "active" : ""
                                }`}
                                onClick={() => {
                                  setSelectedModel(model.id);
                                  setSelectedModelProvider(
                                    String(model.provider || "").toLowerCase()
                                  );
                                  setIsModelMenuOpen(false);
                                }}
                              >
                                {model.name || model.label || model.id}
                              </button>
                            ))}
                          </div>
                        ))}
                        {/* Discoverability hint: Claude Agent is a separate page */}
                        <a
                          href="/claude-agent"
                          className="dropdown-item ca-dropdown-link"
                          style={{
                            display: "block", padding: "10px 12px",
                            borderTop: "1px solid var(--ca-border-subtle, #eee)",
                            fontSize: "12px",
                          }}
                          onClick={() => setIsModelMenuOpen(false)}
                        >
                          💡 Looking for Claude Agent? <strong>Use the dedicated workspace →</strong>
                        </a>
                      </div>
                    )}
                  </div>
                  {chatError && (
                    <div className="chat-error-overlay">{chatError}</div>
                  )}
                  <div className="chat-input-container">
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                    <div className="tiptap-wrapper">
                      <EditorContent editor={editor} />
                      {isImprovingPrompt && (
                        <div className="prompt-improving-overlay">
                          <ImprovingDotsLabel />
                        </div>
                      )}
                    </div>
                    <div className="buttons-container">
                      <button
                        className="circular-button plus-button"
                        disabled={!isConnected}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <img src={plusIcon} alt="Add" className="plus-icon" />
                      </button>
                      <PromptToolsButton onAction={handleImproveAction} />
                      <button
                        className={`web-search-button ${
                          webSearchEnabled ? "enabled" : "disabled"
                        }`}
                        onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                        title={
                          webSearchEnabled
                            ? "Web search enabled"
                            : "Web search disabled"
                        }
                      >
                        <img
                          src={webSearchEnabled ? globeIcon : globeOffIcon}
                          alt="Web Search"
                          className="web-search-icon"
                        />
                      </button>
                      {isResponding ? (
                        <button
                          className="circular-button stop-button"
                          onClick={handleStopResponse}
                          title="Stop response"
                        >
                          <div className="stop-icon"></div>
                        </button>
                      ) : (
                        <button
                          className="circular-button send-button"
                          disabled={
                            !isConnected || isImprovingPrompt ||
                            (!editorHasContent && attachedFiles.length === 0)
                          }
                          onClick={handleSendMessage}
                        >
                          <img src={arrow} alt="Send" className="send-icon" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Chat;
